import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { equipmentService } from '../../../../services/equipmentService';
import { assignmentService } from '../../../../services/assignmentService';
import projectService from '../../../../services/projectService';
import notificationService from '../../../../services/notificationService';
import { getProjectApprovalDetails, ProjectApprovalDetails } from '../../../../services/projectApprovalService';
import { toast } from 'react-toastify';
import { motion, AnimatePresence } from 'framer-motion';
import Select from '../../../../components/ui/Select';
import ManageProjectResources from './ManageProjectResources';
import RightDrawer from '../../../../shared/components/form/RightDrawer';
import { groupEquipmentByBaseName, parseEquipmentName, getEquipmentZone, getEquipmentSubLocation } from '../../utils/equipmentUtils';
import ProjectProgressHeader from '../../components/ProjectProgressHeader';
import { emitProjectProgressUpdate } from '../../utils/projectProgressEvents';

// NOTE: This component currently handles cases where the assignment service endpoints
// are not fully implemented on the backend (returning 404 errors). The component
// gracefully falls back to equipment service and sets default values to prevent
// runtime errors while maintaining functionality.
import {
  Users,
  HardDrive,
  User,
  UserCheck,
  Building2,
  Loader2,
  CheckCircle,
  Shield,
  Eye,
  Info,
  Package,
  ChevronRight,
  ChevronDown,
  RefreshCw,
  Search,
  Filter,
  Phone,
  Briefcase,
  Zap,
  Wind,
  Sun,
  Activity,
  Layers,
  AlertCircle
} from 'lucide-react';

// Modern Clean Brand Colors - Matching Projects page
const BRAND_COLORS = {
  primary: "#339274",          // Primary Green
  primaryHover: "#29795f",     // Darker Green for hover
  primaryLight: "#F8FCFA",     // Very subtle green background
  primarySoft: "#EDF7F2",      // Soft green background
  accent: "#A3CD39",           // Lime accent
  dark: "#007F66",             // Deep teal
  success: "#10B981",
  successLight: "#F0FDF4",
  error: "#EF4444",
  errorLight: "#FEF2F2",
  warning: "#F59E0B",
  warningLight: "#FFFBEB",
  info: "#3B82F6",
  infoLight: "#EFF6FF",
};

interface Assignee {
  _id: string;
  name: string;
  email: string;
  mobile?: string;
  role: {
    _id: string;
    name: string;
  };
  assignedProjects?: Array<{
    projectId: string;
    projectName: string;
    equipmentCount: number;
  }>;
  totalProjectsAssigned?: number;
  totalEquipmentAssigned?: number;
  lastAssignmentUpdate?: string;
  // Fields from ResourceApprovalDetail
  allocatedEquipment?: number;
  otherProjects?: Array<{
    projectId: string;
    projectName: string;
    equipmentCount: number;
    role: string;
  }>;
}

interface Assignment {
  _id: string;
  projectId: string;
  assignmentType: string;
  targetId: string;
  assigneeId: string;
  assigneeName: string;
  assigneeRole: string;
  assignmentDate: string;
  status: string;
  isActive: boolean;
}

interface ProjectAssignmentsProps {
  isOpen?: boolean;
  onClose?: () => void;
  projectId?: string;
}

interface ResourceInsight {
  total: number;
  approved: number;
  rejected: number;
  pending: number;
  supervisors: Set<string>;
  engineers: Set<string>;
}

// Helper for HierarchyGraph
const normalizeIdHelper = (value: any): string | null => {
  if (!value) return null;
  if (typeof value === 'string') return value;
  if (typeof value === 'object') {
    if (value._id) return String(value._id);
    if (value.id) return String(value.id);
  }
  try {
    return String(value);
  } catch (error) {
    return null;
  }
};

const HierarchyGraph = ({ supervisors, engineers, equipment, onSelectMember, onEquipmentCountClick, showRejectedOnly, setShowRejectedOnly, resourceApprovalData }: any) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  // Initialize all supervisors as expanded by default
  useEffect(() => {
    if (supervisors.length > 0) {
      const initialExpanded: Record<string, boolean> = {};
      supervisors.forEach((s: any) => {
        if (s._id) initialExpanded[s._id] = true;
      });
      setExpanded(prev => ({ ...prev, ...initialExpanded }));
    }
  }, [supervisors.length]);

  const toggleExpand = (id: string) => {
    setExpanded(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const treeData = useMemo(() => {
    const processedEngineers = new Set();
    const supervisorNodes = supervisors.map((sup: any) => {
      const supId = normalizeIdHelper(sup._id);

      // Find equipment for this supervisor
      const supEquipment = equipment.filter((eq: any) => {
        const eqSupId = normalizeIdHelper(eq.siteSupervisorId) ||
          (eq.assignedTo?.role === 'Site Supervisor' ? normalizeIdHelper(eq.assignedTo?.id) : null);
        return eqSupId === supId;
      });

      // Filter for direct assignments (no engineer)
      const directSupEquipment = supEquipment.filter((eq: any) => {
        const engId = normalizeIdHelper(eq.siteEngineerId) ||
          (eq.assignedTo?.role === 'Site Engineer' ? normalizeIdHelper(eq.assignedTo?.id) : null);
        return !engId;
      });

      // Group by engineer
      const engineerMap = new Map();
      supEquipment.forEach((eq: any) => {
        const engId = normalizeIdHelper(eq.siteEngineerId) ||
          (eq.assignedTo?.role === 'Site Engineer' ? normalizeIdHelper(eq.assignedTo?.id) : null);

        if (engId) {
          processedEngineers.add(engId);
          if (!engineerMap.has(engId)) {
            const engineer = engineers.find((e: any) => normalizeIdHelper(e._id) === engId);
            if (engineer) {
              engineerMap.set(engId, {
                data: engineer,
                equipment: []
              });
            }
          }
          if (engineerMap.has(engId)) {
            engineerMap.get(engId).equipment.push(eq);
          }
        }
      });

      return {
        data: sup,
        engineers: Array.from(engineerMap.values()),
        totalEquipment: directSupEquipment.length,
        directEquipment: directSupEquipment,
        totalProjects: sup.totalProjectsAssigned || 1
      };
    });

    // Find independent engineers (not mapped to any supervisor via equipment)
    const independentEngineers = engineers
      .filter((eng: any) => !processedEngineers.has(normalizeIdHelper(eng._id)))
      .map((eng: any) => {
        const engId = normalizeIdHelper(eng._id);
        const engEquipment = equipment.filter((eq: any) => {
          const eqEngId = normalizeIdHelper(eq.siteEngineerId) ||
            (eq.assignedTo?.role === 'Site Engineer' ? normalizeIdHelper(eq.assignedTo?.id) : null);
          return eqEngId === engId;
        });

        return {
          data: eng,
          equipment: engEquipment
        };
      });

    return { supervisorNodes, independentEngineers };
  }, [supervisors, engineers, equipment]);

  const filteredTree = useMemo(() => {
    // First apply rejection filter if enabled
    let nodes = treeData.supervisorNodes;
    let independents = treeData.independentEngineers;

    if (showRejectedOnly) {
      nodes = nodes.filter((node: any) => {
        const isSupervisorRejected = node.data.approvalStatus === 'rejected';
        const hasRejectedEngineers = node.engineers.some((eng: any) => eng.data.approvalStatus === 'rejected');

        // If supervisor is rejected, keep them and all their engineers (to show context)
        // If supervisor is NOT rejected, but has rejected engineers, keep supervisor but filter engineers
        if (isSupervisorRejected) return true;
        if (hasRejectedEngineers) return true;

        return false;
      }).map((node: any) => {
        // If supervisor is NOT rejected, filter their engineers list to show only rejected ones
        if (node.data.approvalStatus !== 'rejected') {
          return {
            ...node,
            engineers: node.engineers.filter((eng: any) => eng.data.approvalStatus === 'rejected')
          };
        }
        return node;
      });

      independents = independents.filter((eng: any) => eng.data.approvalStatus === 'rejected');
    }

    if (!searchQuery) return { supervisorNodes: nodes, independentEngineers: independents };

    const lowerQuery = searchQuery.toLowerCase();

    const filteredSupervisors = nodes.map((node: any) => {
      const supMatch = node.data.name.toLowerCase().includes(lowerQuery);
      const matchingEngineers = node.engineers.filter((eng: any) =>
        eng.data.name.toLowerCase().includes(lowerQuery) ||
        eng.equipment.some((eq: any) => (eq.equipmentName || eq.name).toLowerCase().includes(lowerQuery))
      ).map((eng: any) => ({
        ...eng,
        equipment: eng.equipment.filter((eq: any) =>
          supMatch ||
          eng.data.name.toLowerCase().includes(lowerQuery) ||
          (eq.equipmentName || eq.name).toLowerCase().includes(lowerQuery)
        )
      }));

      if (supMatch || matchingEngineers.length > 0) {
        return {
          ...node,
          engineers: matchingEngineers
        };
      }
      return null;
    }).filter(Boolean);

    const filteredIndependent = independents.filter((eng: any) =>
      eng.data.name.toLowerCase().includes(lowerQuery) ||
      eng.equipment.some((eq: any) => (eq.equipmentName || eq.name).toLowerCase().includes(lowerQuery))
    );

    return { supervisorNodes: filteredSupervisors, independentEngineers: filteredIndependent };
  }, [treeData, searchQuery, showRejectedOnly]);

  return (
    <div className="bg-white/80 backdrop-blur-xl rounded-2xl border border-white/20 shadow-xl overflow-hidden flex flex-col h-full">
      {/* Header */}
      <div className="p-5 border-b border-gray-100 bg-gradient-to-r from-gray-50/80 to-white/80 flex flex-col sm:flex-row gap-4 justify-between items-center flex-shrink-0">
        <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
          <div className="p-2 bg-emerald-100/50 rounded-lg text-emerald-600">
            <Users size={20} />
          </div>
          Team Hierarchy
        </h3>

        <div className="flex items-center gap-3">
          {/* Show Rejected Toggle */}
          {(resourceApprovalData?.hierarchy?.some((s: { approvalStatus: string; }) => s.approvalStatus === 'rejected') ||
            resourceApprovalData?.independentEngineers?.some((e: { approvalStatus: string; }) => e.approvalStatus === 'rejected')) && (
              <button
                onClick={() => setShowRejectedOnly(!showRejectedOnly)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium transition-colors border ${showRejectedOnly
                  ? 'bg-red-50 text-red-700 border-red-200'
                  : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                  }`}
              >
                <Filter size={16} className={showRejectedOnly ? 'text-red-600' : 'text-gray-500'} />
                {showRejectedOnly ? 'Rejected Only' : 'Show Rejected'}
              </button>
            )}

          <div className="relative w-full sm:w-64 group">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-emerald-500 transition-colors" size={18} />
            <input
              type="text"
              placeholder="Search team or equipment..."
              className="w-full pl-10 pr-4 py-2.5 text-sm bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 outline-none transition-all shadow-sm"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Tree Content */}
      <div className="p-6 bg-gray-50/30 overflow-y-auto custom-scrollbar flex-1 relative min-h-[400px]">
        {filteredTree.supervisorNodes.length === 0 && filteredTree.independentEngineers.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-gray-400 h-full">
            <Search size={48} className="mb-4 opacity-20" />
            <p className="text-lg font-medium">No matching results found</p>
            <p className="text-sm">Try adjusting your search terms</p>
          </div>
        ) : (
          <div className="space-y-6">

            {filteredTree.supervisorNodes.map((node: any) => (
              <motion.div
                key={node.data._id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="relative group/supervisor"
              >
                {/* Supervisor Card */}
                <div className="relative z-10">
                  <div
                    className={`bg-white rounded-xl border ${node.data.approvalStatus === 'rejected' ? 'border-red-300 ring-1 ring-red-100' : 'border-gray-200'} shadow-sm hover:shadow-lg hover:border-emerald-200 transition-all duration-300 cursor-pointer overflow-hidden`}
                    onClick={() => toggleExpand(node.data._id)}
                  >
                    <div className="p-4 flex items-center gap-5">
                      <div className="relative">
                        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-50 to-teal-50 text-emerald-600 flex items-center justify-center font-bold text-xl border border-emerald-100 shadow-inner group-hover/supervisor:scale-105 transition-transform duration-300">
                          {node.data.name.charAt(0)}
                        </div>
                        <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-white rounded-full flex items-center justify-center shadow-md border border-gray-100">
                          <Shield size={12} className="text-emerald-600" />
                        </div>
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <h4 className="font-bold text-gray-900 text-lg truncate pr-4">{node.data.name}</h4>
                          <div className={`p-1 rounded-full hover:bg-gray-100 transition-colors duration-300 ${expanded[node.data._id] ? 'rotate-180 bg-gray-100' : ''}`}>
                            <ChevronDown className="text-gray-400" size={20} />
                          </div>
                        </div>

                        <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                          <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-gray-50 border border-gray-100">
                            <Briefcase size={14} className="text-gray-400" />
                            <span className="font-medium text-gray-700">{node.totalProjects}</span> Projects
                          </span>
                          <button
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-700 hover:bg-emerald-600 hover:text-white hover:border-emerald-600 transition-all duration-200 shadow-sm hover:shadow-md transform hover:scale-105 group/supbadge cursor-pointer"
                            onClick={(e) => {
                              e.stopPropagation();
                              const eqIds = node.directEquipment?.map((eq: any) => eq._id || eq.id) || [];
                              if (eqIds.length > 0) {
                                onEquipmentCountClick(eqIds);
                              }
                            }}
                            title="Click to highlight all supervised equipment on map"
                          >
                            <Zap size={14} className="group-hover/supbadge:animate-pulse" />
                            <span className="font-bold">{node.totalEquipment}</span> Equipment
                          </button>
                          {node.data.mobile && (
                            <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-green-50 border border-green-100 text-green-700">
                              <Phone size={14} className="text-green-600" />
                              <span className="font-medium">{node.data.mobile}</span>
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Rejection Reason */}
                    {node.data.approvalStatus === 'rejected' && (
                      <div className="px-4 pb-4 pt-0">
                        <div className="flex items-start gap-3 text-sm text-red-700 bg-red-50/80 p-3 rounded-xl border border-red-100">
                          <AlertCircle size={18} className="mt-0.5 flex-shrink-0 text-red-600" />
                          <div className="flex-1">
                            <span className="font-bold block text-red-800 mb-0.5">Rejected by Head</span>
                            <span className="text-red-600 leading-relaxed">{node.data.approverComments || 'No reason provided. Please check with the approver.'}</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Engineers List (Collapsible) */}
                <AnimatePresence>
                  {expanded[node.data._id] && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden"
                    >
                      <div className="pl-8 pt-4 space-y-3 relative">
                        {/* Vertical Connector Line */}
                        <div className="absolute left-[2.25rem] top-0 bottom-6 w-0.5 bg-gradient-to-b from-emerald-200 to-gray-200"></div>

                        {node.engineers.length === 0 ? (
                          <div className="pl-10 py-3 text-sm text-gray-400 italic flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full bg-gray-300"></div>
                            No engineers assigned
                          </div>
                        ) : (
                          node.engineers.map((engNode: any, idx: number) => (
                            <motion.div
                              key={engNode.data._id}
                              initial={{ x: -20, opacity: 0 }}
                              animate={{ x: 0, opacity: 1 }}
                              transition={{ delay: idx * 0.05 }}
                              className="relative pl-10"
                            >
                              {/* Horizontal Connector */}
                              <div className="absolute left-[2.25rem] top-1/2 -translate-y-1/2 w-8 h-0.5 bg-gray-200">
                                <div className="absolute right-0 top-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-gray-300"></div>
                              </div>

                              {/* Engineer Card */}
                              <div
                                className={`bg-white rounded-xl border ${engNode.data.approvalStatus === 'rejected' ? 'border-red-300 ring-1 ring-red-100' : 'border-gray-200'} p-3 hover:border-blue-300 hover:shadow-md transition-all cursor-pointer group/engineer`}
                                onClick={() => onSelectMember(engNode.data)}
                              >
                                <div className="flex items-center gap-4">
                                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-50 to-indigo-50 text-blue-600 flex items-center justify-center font-bold border border-blue-100 group-hover/engineer:scale-110 transition-transform">
                                    {engNode.data.name.charAt(0)}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center justify-between mb-0.5">
                                      <h5 className="font-semibold text-gray-800 truncate">{engNode.data.name}</h5>
                                      <button
                                        className="text-xs bg-blue-50 text-blue-600 px-3 py-1.5 rounded-full font-bold hover:bg-blue-600 hover:text-white transition-all duration-200 z-20 relative flex items-center gap-1.5 shadow-sm hover:shadow-md transform hover:scale-105 group/badge"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          const eqIds = engNode.equipment.map((eq: any) => eq._id || eq.id);
                                          onEquipmentCountClick(eqIds);
                                        }}
                                        title="Click to highlight equipment on map"
                                      >
                                        <HardDrive size={12} className="group-hover/badge:animate-pulse" />
                                        <span>{engNode.equipment.length}</span>
                                      </button>
                                    </div>
                                    <div className="flex items-center gap-3 text-xs text-gray-500">
                                      {engNode.data.mobile && (
                                        <span className="flex items-center gap-1 px-2 py-0.5 rounded-md bg-green-50 border border-green-100 text-green-700">
                                          <Phone size={12} className="text-green-600" />
                                          <span className="font-medium">{engNode.data.mobile}</span>
                                        </span>
                                      )}
                                      <span className="flex items-center gap-1">
                                        <Briefcase size={12} /> {engNode.data.totalProjectsAssigned || 1} Projects
                                      </span>
                                    </div>
                                  </div>
                                </div>

                                {/* Rejection Reason */}
                                {engNode.data.approvalStatus === 'rejected' && (
                                  <div className="mt-3 flex items-start gap-2 text-xs text-red-700 bg-red-50 p-2.5 rounded-lg border border-red-100">
                                    <AlertCircle size={14} className="mt-0.5 flex-shrink-0 text-red-600" />
                                    <div className="flex-1">
                                      <span className="font-bold block text-red-800 mb-0.5">Rejected:</span>
                                      <span className="leading-relaxed">{engNode.data.approverComments || 'No reason provided.'}</span>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </motion.div>
                          ))
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}

            {/* Independent Engineers Section */}
            {filteredTree.independentEngineers.length > 0 && (
              <div className="pt-6 border-t border-gray-200">
                <h4 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-4 pl-2 flex items-center gap-2">
                  <Users size={16} />
                  Other Site Engineers
                </h4>
                <div className="space-y-3">
                  {filteredTree.independentEngineers.map((engNode: any, idx: number) => (
                    <motion.div
                      key={engNode.data._id}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: idx * 0.05 }}
                      className={`bg-white rounded-xl border ${engNode.data.approvalStatus === 'rejected' ? 'border-red-300 ring-1 ring-red-100' : 'border-gray-200'} p-3 hover:border-blue-300 hover:shadow-md transition-all cursor-pointer group/engineer`}
                      onClick={() => onSelectMember(engNode.data)}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-gray-50 to-slate-100 text-gray-600 flex items-center justify-center font-bold border border-gray-200 group-hover/engineer:scale-110 transition-transform">
                          {engNode.data.name.charAt(0)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-0.5">
                            <h5 className="font-semibold text-gray-800 truncate">{engNode.data.name}</h5>
                            <button
                              className="text-xs bg-blue-50 text-blue-600 px-3 py-1.5 rounded-full font-bold hover:bg-blue-600 hover:text-white transition-all duration-200 z-20 relative flex items-center gap-1.5 shadow-sm hover:shadow-md transform hover:scale-105 group/badge"
                              onClick={(e) => {
                                e.stopPropagation();
                                const eqIds = engNode.equipment.map((eq: any) => eq._id || eq.id);
                                onEquipmentCountClick(eqIds);
                              }}
                              title="Click to highlight equipment on map"
                            >
                              <HardDrive size={12} className="group-hover/badge:animate-pulse" />
                              <span>{engNode.equipment.length}</span>
                            </button>
                          </div>
                          <div className="flex items-center gap-3 text-xs text-gray-500">
                            {engNode.data.mobile && (
                              <span className="flex items-center gap-1 px-2 py-0.5 rounded-md bg-green-50 border border-green-100 text-green-700">
                                <Phone size={12} className="text-green-600" />
                                <span className="font-medium">{engNode.data.mobile}</span>
                              </span>
                            )}
                            <span className="flex items-center gap-1">
                              <Briefcase size={12} /> {engNode.data.totalProjectsAssigned || 1} Projects
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Rejection Reason */}
                      {engNode.data.approvalStatus === 'rejected' && (
                        <div className="mt-3 flex items-start gap-2 text-xs text-red-700 bg-red-50 p-2.5 rounded-lg border border-red-100">
                          <AlertCircle size={14} className="mt-0.5 flex-shrink-0 text-red-600" />
                          <div className="flex-1">
                            <span className="font-bold block text-red-800 mb-0.5">Rejected:</span>
                            <span className="leading-relaxed">{engNode.data.approverComments || 'No reason provided.'}</span>
                          </div>
                        </div>
                      )}
                    </motion.div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const ArchitectureAssignmentView = ({ equipment, selectedEquipmentIds, onSelectEquipment, onSelectAllAsset, onSelectMember }: any) => {
  const [expandedAssets, setExpandedAssets] = useState<Record<string, boolean>>({});
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'assigned' | 'unassigned'>('all');

  // Group equipment by Asset Name (Inverter)
  const assetGroups = useMemo(() => {
    const groups: Record<string, any[]> = {};
    equipment.forEach((eq: any) => {
      const assetName = eq.assetName || 'Unassigned Equipment';
      if (!groups[assetName]) groups[assetName] = [];
      groups[assetName].push(eq);
    });
    return groups;
  }, [equipment]);

  const filteredGroups = useMemo(() => {
    let result = assetGroups;

    // Apply status filter
    if (filterStatus !== 'all') {
      const filtered: Record<string, any[]> = {};
      Object.entries(assetGroups).forEach(([asset, items]) => {
        const filteredItems = items.filter((item: any) => {
          if (filterStatus === 'assigned') return item.isAssigned;
          if (filterStatus === 'unassigned') return !item.isAssigned;
          return true;
        });
        if (filteredItems.length > 0) {
          filtered[asset] = filteredItems;
        }
      });
      result = filtered;
    }

    // Apply search filter
    if (!searchQuery) return result;
    const lowerQuery = searchQuery.toLowerCase();
    const searchResult: Record<string, any[]> = {};

    Object.entries(result).forEach(([asset, items]) => {
      if (asset.toLowerCase().includes(lowerQuery)) {
        searchResult[asset] = items;
      } else {
        const matchingItems = items.filter((item: any) =>
          (item.equipmentName || item.name).toLowerCase().includes(lowerQuery) ||
          (item.assignedTo?.name || '').toLowerCase().includes(lowerQuery) ||
          (item.makeAndModel || '').toLowerCase().includes(lowerQuery)
        );
        if (matchingItems.length > 0) {
          searchResult[asset] = matchingItems;
        }
      }
    });
    return searchResult;
  }, [assetGroups, searchQuery, filterStatus]);

  // Calculate overall stats
  const overallStats = useMemo(() => {
    const total = equipment.length;
    const assigned = equipment.filter((e: any) => e.isAssigned).length;
    const unassigned = total - assigned;
    return { total, assigned, unassigned };
  }, [equipment]);

  const toggleExpand = (assetName: string) => {
    setExpandedAssets(prev => ({ ...prev, [assetName]: !prev[assetName] }));
  };

  // Auto-expand if searching
  useEffect(() => {
    if (searchQuery) {
      const allExpanded: Record<string, boolean> = {};
      Object.keys(filteredGroups).forEach(key => allExpanded[key] = true);
      setExpandedAssets(allExpanded);
    }
  }, [searchQuery, filteredGroups]);

  // Auto-expand all by default
  useEffect(() => {
    const allExpanded: Record<string, boolean> = {};
    Object.keys(assetGroups).forEach(key => allExpanded[key] = true);
    setExpandedAssets(allExpanded);
  }, [assetGroups]);

  return (
    <div className="bg-white/80 backdrop-blur-xl rounded-2xl border border-white/20 shadow-xl overflow-hidden flex flex-col h-full">
      {/* Header */}
      <div className="p-5 border-b border-gray-100 bg-gradient-to-r from-gray-50/80 to-white/80 flex flex-col gap-4 flex-shrink-0">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
            <div className="p-2 bg-blue-100/50 rounded-lg text-blue-600">
              <Layers size={20} />
            </div>
            Architecture View - Equipment by Assets
          </h3>
        </div>

        {/* Stats Bar */}
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 rounded-lg border border-blue-100">
            <Package size={16} className="text-blue-600" />
            <span className="text-sm font-semibold text-blue-700">{overallStats.total}</span>
            <span className="text-xs text-blue-600">Total</span>
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 bg-green-50 rounded-lg border border-green-100">
            <CheckCircle size={16} className="text-green-600" />
            <span className="text-sm font-semibold text-green-700">{overallStats.assigned}</span>
            <span className="text-xs text-green-600">Assigned</span>
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 bg-orange-50 rounded-lg border border-orange-100">
            <AlertCircle size={16} className="text-orange-600" />
            <span className="text-sm font-semibold text-orange-700">{overallStats.unassigned}</span>
            <span className="text-xs text-orange-600">Pending</span>
          </div>
        </div>

        {/* Search and Filter */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1 group">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-blue-500 transition-colors" size={18} />
            <input
              type="text"
              placeholder="Search asset, equipment, assignee..."
              className="w-full pl-10 pr-4 py-2.5 text-sm bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all shadow-sm"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setFilterStatus('all')}
              className={`px-4 py-2.5 text-sm font-medium rounded-xl border transition-all ${filterStatus === 'all'
                ? 'bg-blue-50 text-blue-700 border-blue-300'
                : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                }`}
            >
              All
            </button>
            <button
              onClick={() => setFilterStatus('assigned')}
              className={`px-4 py-2.5 text-sm font-medium rounded-xl border transition-all ${filterStatus === 'assigned'
                ? 'bg-green-50 text-green-700 border-green-300'
                : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                }`}
            >
              Assigned
            </button>
            <button
              onClick={() => setFilterStatus('unassigned')}
              className={`px-4 py-2.5 text-sm font-medium rounded-xl border transition-all ${filterStatus === 'unassigned'
                ? 'bg-orange-50 text-orange-700 border-orange-300'
                : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                }`}
            >
              Unassigned
            </button>
          </div>
        </div>
      </div>

      {/* Tree Content */}
      <div className="p-6 bg-gray-50/30 overflow-y-auto custom-scrollbar flex-1 relative min-h-[400px]">
        {Object.keys(filteredGroups).length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-gray-400 h-full">
            <Search size={48} className="mb-4 opacity-20" />
            <p className="text-lg font-medium">No results found</p>
            <p className="text-sm mt-1">Try adjusting your filters or search terms</p>
          </div>
        ) : (
          <div className="space-y-4">
            {Object.entries(filteredGroups).map(([assetName, items]) => {
              const allSelected = items.every((item: any) => selectedEquipmentIds.includes(item._id || item.id));
              const someSelected = items.some((item: any) => selectedEquipmentIds.includes(item._id || item.id));
              const isExpanded = expandedAssets[assetName];
              const assignedCount = items.filter((i: any) => i.isAssigned).length;
              const unassignedCount = items.length - assignedCount;
              const assignedItems = items.filter((i: any) => i.isAssigned);
              const unassignedItems = items.filter((i: any) => !i.isAssigned);

              return (
                <motion.div
                  key={assetName}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`bg-white rounded-xl border overflow-hidden shadow-sm hover:shadow-md transition-shadow ${unassignedCount > 0 ? 'border-orange-200' : 'border-green-200'
                    }`}
                >
                  <div
                    className="p-4 flex items-center gap-4 cursor-pointer hover:bg-gray-50 transition-colors"
                    onClick={() => toggleExpand(assetName)}
                  >
                    <div
                      className="p-1 rounded-lg hover:bg-gray-200 text-gray-400 transition-colors"
                      onClick={(e) => { e.stopPropagation(); toggleExpand(assetName); }}
                    >
                      <ChevronRight size={20} className={`transform transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`} />
                    </div>

                    <div className={`h-12 w-12 rounded-xl flex items-center justify-center ${assignedCount === items.length
                      ? 'bg-gradient-to-br from-green-100 to-emerald-100 text-green-700'
                      : assignedCount > 0
                        ? 'bg-gradient-to-br from-blue-100 to-indigo-100 text-blue-700'
                        : 'bg-gradient-to-br from-orange-100 to-amber-100 text-orange-700'
                      }`}>
                      <Zap size={24} fill={assignedCount === items.length ? "currentColor" : "none"} />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-bold text-gray-900 text-lg">{assetName}</h4>
                        <span className="text-xs px-2.5 py-1 rounded-full bg-gray-100 text-gray-700 font-semibold">
                          {items.length} Items
                        </span>
                      </div>
                      <div className="flex items-center gap-4 text-sm">
                        {assignedCount > 0 && (
                          <span className="flex items-center gap-1.5 text-green-700 font-medium">
                            <CheckCircle size={14} className="text-green-600" />
                            {assignedCount} Assigned
                          </span>
                        )}
                        {unassignedCount > 0 && (
                          <span className="flex items-center gap-1.5 text-orange-700 font-medium">
                            <AlertCircle size={14} className="text-orange-600" />
                            {unassignedCount} Pending
                          </span>
                        )}
                        {assignedCount === items.length && (
                          <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-bold rounded-full">
                            Complete
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <div
                        className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-colors"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          className="w-4 h-4 rounded text-blue-600 focus:ring-blue-500 border-gray-300 cursor-pointer"
                          checked={allSelected}
                          ref={(input) => { if (input) input.indeterminate = someSelected && !allSelected; }}
                          onChange={(e) => onSelectAllAsset(assetName, e.target.checked)}
                        />
                        <span className="text-sm font-medium text-gray-700">Select All</span>
                      </div>
                    </div>
                  </div>

                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div
                        initial={{ height: 0 }}
                        animate={{ height: 'auto' }}
                        exit={{ height: 0 }}
                        className="border-t border-gray-100 bg-gray-50/30"
                      >
                        <div className="p-4 space-y-4">
                          {/* Unassigned Equipment Section */}
                          {unassignedItems.length > 0 && (
                            <div>
                              <div className="flex items-center gap-2 mb-3">
                                <AlertCircle size={16} className="text-orange-600" />
                                <h5 className="text-sm font-bold text-orange-700">Unassigned Equipment ({unassignedItems.length})</h5>
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                                {unassignedItems.map((item: any) => (
                                  <div
                                    key={item._id || item.id}
                                    className={`
                                      flex flex-col gap-2 p-3 rounded-lg border-2 transition-all cursor-pointer
                                      ${selectedEquipmentIds.includes(item._id || item.id)
                                        ? 'bg-blue-50 border-blue-300 shadow-md ring-2 ring-blue-200'
                                        : 'bg-white border-orange-200 hover:border-orange-300 hover:shadow-sm'
                                      }
                                    `}
                                    onClick={() => onSelectEquipment(item._id || item.id, !selectedEquipmentIds.includes(item._id || item.id))}
                                  >
                                    <div className="flex items-start gap-2">
                                      <input
                                        type="checkbox"
                                        className="w-4 h-4 rounded text-blue-600 focus:ring-blue-500 border-gray-300 pointer-events-none mt-0.5"
                                        checked={selectedEquipmentIds.includes(item._id || item.id)}
                                        readOnly
                                      />
                                      <div className="flex-1 min-w-0">
                                        <div className="font-semibold text-sm text-gray-900 truncate" title={item.equipmentName || item.name}>
                                          {item.equipmentName || item.name}
                                        </div>
                                        <div className="text-xs text-gray-500 truncate mt-0.5">
                                          {item.makeAndModel || 'Unknown Model'}
                                        </div>
                                      </div>
                                    </div>
                                    <div className="px-2 py-1 rounded bg-orange-50 text-orange-700 text-xs font-medium text-center border border-orange-200">
                                      Unassigned - Ready to Assign
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Assigned Equipment Section */}
                          {assignedItems.length > 0 && (
                            <div>
                              <div className="flex items-center gap-2 mb-3">
                                <CheckCircle size={16} className="text-green-600" />
                                <h5 className="text-sm font-bold text-green-700">Assigned Equipment ({assignedItems.length})</h5>
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                                {assignedItems.map((item: any) => (
                                  <div
                                    key={item._id || item.id}
                                    className={`
                                      flex flex-col gap-2 p-3 rounded-lg border-2 transition-all cursor-pointer
                                      ${selectedEquipmentIds.includes(item._id || item.id)
                                        ? 'bg-blue-50 border-blue-300 shadow-md ring-2 ring-blue-200'
                                        : 'bg-white border-green-200 hover:border-green-300 hover:shadow-sm'
                                      }
                                    `}
                                    onClick={() => onSelectEquipment(item._id || item.id, !selectedEquipmentIds.includes(item._id || item.id))}
                                  >
                                    <div className="flex items-start gap-2">
                                      <input
                                        type="checkbox"
                                        className="w-4 h-4 rounded text-blue-600 focus:ring-blue-500 border-gray-300 pointer-events-none mt-0.5"
                                        checked={selectedEquipmentIds.includes(item._id || item.id)}
                                        readOnly
                                      />
                                      <div className="flex-1 min-w-0">
                                        <div className="font-semibold text-sm text-gray-900 truncate" title={item.equipmentName || item.name}>
                                          {item.equipmentName || item.name}
                                        </div>
                                        <div className="text-xs text-gray-500 truncate mt-0.5">
                                          {item.makeAndModel || 'Unknown Model'}
                                        </div>
                                      </div>
                                    </div>
                                    {item.assignedTo && (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          if (onSelectMember && item.assignedTo) {
                                            onSelectMember(item.assignedTo);
                                          }
                                        }}
                                        className="flex items-center gap-1.5 px-2 py-1.5 rounded bg-green-50 text-green-700 border border-green-200 text-xs font-medium hover:bg-green-100 transition-colors w-full justify-center"
                                      >
                                        <UserCheck size={12} />
                                        <span className="truncate">{item.assignedTo.name || 'Assigned'}</span>
                                      </button>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

const ResourcesAssignmentView = ({
  hierarchicalAssignees,
  equipment,
  selectedEquipmentIds,
  onSelectEquipment,
  onSelectMember,
  onEquipmentCountClick,
  normalizeId
}: any) => {
  const [expandedResources, setExpandedResources] = useState<Record<string, boolean>>({});
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRole, setFilterRole] = useState<'all' | 'supervisor' | 'engineer'>('all');

  // Get all resources with their equipment
  const resourcesWithEquipment = useMemo(() => {
    const resources: any[] = [];

    // Add supervisors
    hierarchicalAssignees.supervisors.forEach((sup: any) => {
      const supId = normalizeId(sup._id);
      const supEquipment = equipment.filter((eq: any) => {
        const eqSupId = normalizeId(eq.siteSupervisorId) ||
          (eq.assignedTo?.role === 'Site Supervisor' ? normalizeId(eq.assignedTo?.id) : null);
        return eqSupId === supId;
      });

      resources.push({
        ...sup,
        role: 'Site Supervisor',
        equipment: supEquipment,
        directEquipment: supEquipment.filter((eq: any) => {
          const engId = normalizeId(eq.siteEngineerId) ||
            (eq.assignedTo?.role === 'Site Engineer' ? normalizeId(eq.assignedTo?.id) : null);
          return !engId;
        })
      });
    });

    // Add independent engineers
    hierarchicalAssignees.independentEngineers.forEach((eng: any) => {
      const engId = normalizeId(eng._id);
      const engEquipment = equipment.filter((eq: any) => {
        const eqEngId = normalizeId(eq.siteEngineerId) ||
          (eq.assignedTo?.role === 'Site Engineer' ? normalizeId(eq.assignedTo?.id) : null);
        return eqEngId === engId;
      });

      resources.push({
        ...eng,
        role: 'Site Engineer',
        equipment: engEquipment,
        directEquipment: engEquipment
      });
    });

    return resources;
  }, [hierarchicalAssignees, equipment, normalizeId]);

  const filteredResources = useMemo(() => {
    let result = resourcesWithEquipment;

    // Apply role filter
    if (filterRole !== 'all') {
      result = result.filter((r: any) => {
        if (filterRole === 'supervisor') return r.role === 'Site Supervisor';
        if (filterRole === 'engineer') return r.role === 'Site Engineer';
        return true;
      });
    }

    // Apply search filter
    if (!searchQuery) return result;
    const lowerQuery = searchQuery.toLowerCase();
    return result.filter((r: any) => {
      if (r.name.toLowerCase().includes(lowerQuery)) return true;
      if (r.email?.toLowerCase().includes(lowerQuery)) return true;
      if (r.mobile?.toLowerCase().includes(lowerQuery)) return true;
      if (r.equipment.some((eq: any) =>
        (eq.equipmentName || eq.name).toLowerCase().includes(lowerQuery)
      )) return true;
      return false;
    });
  }, [resourcesWithEquipment, searchQuery, filterRole]);

  const toggleExpand = (resourceId: string) => {
    setExpandedResources(prev => ({ ...prev, [resourceId]: !prev[resourceId] }));
  };

  // Auto-expand all by default
  useEffect(() => {
    const allExpanded: Record<string, boolean> = {};
    resourcesWithEquipment.forEach((r: any) => {
      if (r._id) allExpanded[r._id] = true;
    });
    setExpandedResources(allExpanded);
  }, [resourcesWithEquipment.length]);

  const totalResources = resourcesWithEquipment.length;
  const totalEquipment = resourcesWithEquipment.reduce((sum, r) => sum + r.equipment.length, 0);

  return (
    <div className="bg-white/80 backdrop-blur-xl rounded-2xl border border-white/20 shadow-xl overflow-hidden flex flex-col h-full">
      {/* Header */}
      <div className="p-5 border-b border-gray-100 bg-gradient-to-r from-gray-50/80 to-white/80 flex flex-col gap-4 flex-shrink-0">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
            <div className="p-2 bg-emerald-100/50 rounded-lg text-emerald-600">
              <Users size={20} />
            </div>
            Resources & Assignments
          </h3>
        </div>

        {/* Stats Bar */}
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-50 rounded-lg border border-emerald-100">
            <Users size={16} className="text-emerald-600" />
            <span className="text-sm font-semibold text-emerald-700">{totalResources}</span>
            <span className="text-xs text-emerald-600">Resources</span>
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 rounded-lg border border-blue-100">
            <HardDrive size={16} className="text-blue-600" />
            <span className="text-sm font-semibold text-blue-700">{totalEquipment}</span>
            <span className="text-xs text-blue-600">Equipment Assigned</span>
          </div>
        </div>

        {/* Search and Filter */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1 group">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-emerald-500 transition-colors" size={18} />
            <input
              type="text"
              placeholder="Search resource, equipment..."
              className="w-full pl-10 pr-4 py-2.5 text-sm bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 outline-none transition-all shadow-sm"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setFilterRole('all')}
              className={`px-4 py-2.5 text-sm font-medium rounded-xl border transition-all ${filterRole === 'all'
                ? 'bg-emerald-50 text-emerald-700 border-emerald-300'
                : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                }`}
            >
              All
            </button>
            <button
              onClick={() => setFilterRole('supervisor')}
              className={`px-4 py-2.5 text-sm font-medium rounded-xl border transition-all ${filterRole === 'supervisor'
                ? 'bg-blue-50 text-blue-700 border-blue-300'
                : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                }`}
            >
              Supervisors
            </button>
            <button
              onClick={() => setFilterRole('engineer')}
              className={`px-4 py-2.5 text-sm font-medium rounded-xl border transition-all ${filterRole === 'engineer'
                ? 'bg-purple-50 text-purple-700 border-purple-300'
                : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                }`}
            >
              Engineers
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6 bg-gray-50/30 overflow-y-auto custom-scrollbar flex-1 relative min-h-[400px]">
        {filteredResources.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-gray-400 h-full">
            <Users size={48} className="mb-4 opacity-20" />
            <p className="text-lg font-medium">No resources found</p>
            <p className="text-sm mt-1">Try adjusting your filters or search terms</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredResources.map((resource: any) => {
              const isExpanded = expandedResources[resource._id];
              const equipmentCount = resource.equipment.length;
              const directCount = resource.directEquipment?.length || 0;

              return (
                <motion.div
                  key={resource._id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow"
                >
                  <div
                    className="p-4 flex items-center gap-4 cursor-pointer hover:bg-gray-50 transition-colors"
                    onClick={() => toggleExpand(resource._id)}
                  >
                    <div
                      className="p-1 rounded-lg hover:bg-gray-200 text-gray-400 transition-colors"
                      onClick={(e) => { e.stopPropagation(); toggleExpand(resource._id); }}
                    >
                      <ChevronRight size={20} className={`transform transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`} />
                    </div>

                    <div className={`h-12 w-12 rounded-xl flex items-center justify-center font-bold text-xl ${resource.role === 'Site Supervisor'
                      ? 'bg-gradient-to-br from-emerald-100 to-teal-100 text-emerald-700'
                      : 'bg-gradient-to-br from-blue-100 to-indigo-100 text-blue-700'
                      }`}>
                      {resource.name.charAt(0)}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-bold text-gray-900 text-lg">{resource.name}</h4>
                        {resource.role === 'Site Supervisor' && (
                          <Shield size={16} className="text-emerald-600" />
                        )}
                        <span className={`text-xs px-2.5 py-1 rounded-full font-semibold ${resource.role === 'Site Supervisor'
                          ? 'bg-emerald-100 text-emerald-700'
                          : 'bg-blue-100 text-blue-700'
                          }`}>
                          {resource.role}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 text-sm">
                        <span className="flex items-center gap-1.5 text-gray-600">
                          <HardDrive size={14} className="text-blue-500" />
                          <span className="font-semibold">{equipmentCount}</span>
                          <span>Equipment</span>
                        </span>
                        {resource.role === 'Site Supervisor' && directCount > 0 && (
                          <>
                            <span className="w-1 h-1 rounded-full bg-gray-300"></span>
                            <span className="text-gray-500">{directCount} Direct</span>
                          </>
                        )}
                        {resource.mobile && (
                          <>
                            <span className="w-1 h-1 rounded-full bg-gray-300"></span>
                            <span className="flex items-center gap-1 text-gray-500">
                              <Phone size={12} />
                              {resource.mobile}
                            </span>
                          </>
                        )}
                      </div>
                    </div>

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (onSelectMember) {
                          onSelectMember(resource);
                        }
                      }}
                      className="px-4 py-2 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-lg hover:bg-emerald-100 transition-colors text-sm font-medium"
                    >
                      View Details
                    </button>
                  </div>

                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div
                        initial={{ height: 0 }}
                        animate={{ height: 'auto' }}
                        exit={{ height: 0 }}
                        className="border-t border-gray-100 bg-gray-50/30"
                      >
                        <div className="p-4">
                          {equipmentCount === 0 ? (
                            <div className="text-center py-8 text-gray-400">
                              <HardDrive size={32} className="mx-auto mb-2 opacity-50" />
                              <p className="text-sm">No equipment assigned</p>
                            </div>
                          ) : (
                            <div className="space-y-3">
                              <div className="flex items-center justify-between mb-3">
                                <h5 className="text-sm font-bold text-gray-700">Assigned Equipment ({equipmentCount})</h5>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (onEquipmentCountClick) {
                                      const eqIds = resource.equipment.map((eq: any) => eq._id || eq.id);
                                      onEquipmentCountClick(eqIds);
                                    }
                                  }}
                                  className="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
                                >
                                  <Eye size={14} />
                                  Highlight on Map
                                </button>
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                                {resource.equipment.map((item: any) => (
                                  <div
                                    key={item._id || item.id}
                                    className={`
                                      flex items-center gap-2 p-2.5 rounded-lg border transition-all cursor-pointer
                                      ${selectedEquipmentIds.includes(item._id || item.id)
                                        ? 'bg-blue-50 border-blue-300 shadow-sm'
                                        : 'bg-white border-gray-200 hover:border-gray-300'
                                      }
                                    `}
                                    onClick={() => onSelectEquipment(item._id || item.id, !selectedEquipmentIds.includes(item._id || item.id))}
                                  >
                                    <input
                                      type="checkbox"
                                      className="w-4 h-4 rounded text-blue-600 focus:ring-blue-500 border-gray-300 pointer-events-none"
                                      checked={selectedEquipmentIds.includes(item._id || item.id)}
                                      readOnly
                                    />
                                    <div className="flex-1 min-w-0">
                                      <div className="font-medium text-sm text-gray-900 truncate" title={item.equipmentName || item.name}>
                                        {item.equipmentName || item.name}
                                      </div>
                                      <div className="text-xs text-gray-500 truncate">
                                        {item.assetName || 'Unknown Asset'}
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

function ProjectAssignments({ isOpen: propIsOpen, onClose, projectId: propProjectId }: ProjectAssignmentsProps) {
  const { projectId: urlProjectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();

  const projectId = propProjectId || urlProjectId || '';
  const isOpen = propIsOpen !== undefined ? propIsOpen : true;

  const handleClose = () => {
    if (onClose) {
      onClose();
    } else {
      navigate(-1);
    }
  };

  // State
  const [loading, setLoading] = useState(false);
  const [project, setProject] = useState<any>(null);
  const [equipment, setEquipment] = useState<any[]>([]);
  const [equipmentLoading, setEquipmentLoading] = useState(false);
  const [availableAssignees, setAvailableAssignees] = useState<Assignee[]>([]);
  const [existingAssignments, setExistingAssignments] = useState<Assignment[]>([]);
  const [selectedEquipmentIds, setSelectedEquipmentIds] = useState<string[]>([]);
  const [resourceApprovalData, setResourceApprovalData] = useState<ProjectApprovalDetails['resources'] | null>(null);
  const [showRejectedOnly, setShowRejectedOnly] = useState(false);


  // Removed checkpoint and equipment info drawer states - focusing only on project equipment assignments
  const [assigneesLoading, setAssigneesLoading] = useState(false);
  const [selectedResourceId, setSelectedResourceId] = useState<string | null>(null);

  // State for expanded equipment groups
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});

  // ==========================================
  // VIEW STATE: Member Details & Resource Insights
  // ==========================================
  const [showMemberDetails, setShowMemberDetails] = useState(false);
  const [selectedMember, setSelectedMember] = useState<Assignee | null>(null);
  const [resourceWorkload, setResourceWorkload] = useState<any>(null);
  const [isWorkloadLoading, setIsWorkloadLoading] = useState(false);
  
  // Resource Insights Dialog State
  const [showResourceInsights, setShowResourceInsights] = useState(false);

  // State for assignment details drawer
  const [showAssignmentDetails, setShowAssignmentDetails] = useState(false);
  const [selectedMemberAssignments, setSelectedMemberAssignments] = useState<Assignment[]>([]);

  // Removed workload information - focusing only on project equipment assignments

  // Always use split view - removed view mode toggle

  // State for ManageProjectResources drawer
  const [showManageResources, setShowManageResources] = useState(false);

  const normalizeId = (value: any): string | null => {
    if (!value) return null;
    if (typeof value === 'string') return value;
    if (typeof value === 'object') {
      if (value._id) return String(value._id);
      if (value.id) return String(value.id);
    }
    try {
      return String(value);
    } catch (error) {
      return null;
    }
  };

  const mergeEquipmentDetails = (baseEquipment: any[], rawEquipment: any[]): any[] => {
    if (!Array.isArray(baseEquipment)) return [];

    const rawMap = new Map<string, any>();
    rawEquipment?.forEach((raw) => {
      const key = normalizeId(raw?._id) || normalizeId(raw?.id) || normalizeId(raw?.equipmentId);
      if (key) {
        rawMap.set(key, raw);
      }
    });

    return baseEquipment.map((equipmentItem) => {
      const key = normalizeId(equipmentItem?._id) || normalizeId(equipmentItem?.id) || normalizeId(equipmentItem?.equipmentId);
      const raw = key ? rawMap.get(key) : undefined;

      const rawEngineerId = normalizeId(raw?.siteEngineerId);
      const rawSupervisorId = normalizeId(raw?.siteSupervisorId);

      const assignedRole = equipmentItem?.assignedTo?.role;
      const assignedToId = normalizeId(equipmentItem?.assignedTo?._id) || normalizeId(equipmentItem?.assignedTo?.id);

      const siteEngineerId = rawEngineerId || (assignedRole === 'Site Engineer' ? assignedToId : null);
      const siteSupervisorId = rawSupervisorId || (assignedRole === 'Site Supervisor' ? assignedToId : null);

      const approvalStatus = (raw?.approvalStatus || equipmentItem?.approvalStatus || 'pending').toString().toLowerCase();

      return {
        ...equipmentItem,
        approvalStatus,
        siteEngineerId,
        siteSupervisorId,
      };
    });
  };

  // Load initial data when drawer opens
  useEffect(() => {
    if (isOpen && projectId) {
      loadProject();
      loadProjectEquipment();
      loadAvailableAssignees();
      loadExistingAssignments();
      loadResourceApprovalData();
    }
  }, [isOpen, projectId]);

  // Load ProjectResourceApproval data (source of truth for approval status)
  const loadResourceApprovalData = async () => {
    if (!projectId) return;
    try {
      const approvalDetails = await getProjectApprovalDetails(projectId);
      setResourceApprovalData(approvalDetails.resources);
    } catch (error: any) {
      console.error('Failed to load resource approval data:', error);
      // Don't show error toast - this is optional data
      setResourceApprovalData(null);
    }
  };

  // Refresh assignments when drawer opens or regains focus (handles case when returning from approval drawer)
  useEffect(() => {
    if (isOpen && projectId) {
      // Refresh immediately when drawer opens
      const refreshData = async () => {
        await Promise.all([
          loadExistingAssignments(),
          loadResourceApprovalData()
        ]);
      };

      // Refresh on visibility change (when tab/window becomes visible)
      const handleVisibilityChange = () => {
        if (!document.hidden && isOpen) {
          loadExistingAssignments();
          loadResourceApprovalData();
        }
      };

      // Refresh on window focus (when user switches back to this tab/window)
      const handleFocus = () => {
        if (isOpen) {
          loadExistingAssignments();
          loadResourceApprovalData();
        }
      };

      refreshData();
      document.addEventListener('visibilitychange', handleVisibilityChange);
      window.addEventListener('focus', handleFocus);

      return () => {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        window.removeEventListener('focus', handleFocus);
      };
    }
  }, [isOpen, projectId]);

  // Manual refresh function
  const handleRefreshAssignments = async () => {
    await Promise.all([
      loadExistingAssignments(),
      loadProjectEquipment(),
      loadAvailableAssignees(),
      loadResourceApprovalData()
    ]);
    toast.success('Assignments refreshed');
  };


  // Call notification unread count API once after 1 hour
  useEffect(() => {
    if (isOpen) {
      const timeoutId = setTimeout(async () => {
        try {
          console.log('Calling notification unread count API after 1 hour...');
          const response = await notificationService.getUnreadCount();
          console.log('Notification unread count response:', response);

          if (response.success) {
            console.log(`Unread notifications count: ${response.data.count}`);
            // You can add additional logic here to handle the unread count
            // For example, update a state variable or show a toast notification
          } else {
            console.error('Failed to get unread count:', response.message);
          }
        } catch (error) {
          console.error('Error calling notification unread count API:', error);
        }
      }, 60 * 60 * 1000); // 1 hour in milliseconds

      // Cleanup timeout on component unmount or when drawer closes
      return () => {
        clearTimeout(timeoutId);
      };
    }
  }, [isOpen]);

  // Removed hover timeout cleanup - no longer needed






  const loadProject = async () => {
    try {
      setLoading(true);
      const response = await projectService.getProjectById(projectId!);
      setProject(response?.data || response);
    } catch (error: any) {
      toast.error(error.message || 'Failed to load project');
    } finally {
      setLoading(false);
    }
  };

  const loadProjectEquipment = async () => {
    try {
      setEquipmentLoading(true);

      // First, get all equipment directly from equipment service (source of truth)
      let rawEquipment: any[] = [];
      try {
        const raw = await equipmentService.getByProject(projectId!);
        rawEquipment = Array.isArray(raw) ? raw : Array.isArray((raw as any)?.data) ? (raw as any).data : [];
        console.log('Raw equipment loaded:', rawEquipment.length, 'items');
      } catch (rawError) {
        console.error('Failed to retrieve equipment list:', rawError);
        toast.error('Failed to load equipment');
        return;
      }

      if (!rawEquipment || rawEquipment.length === 0) {
        setEquipment([]);
        return;
      }

      // Try to enrich with assignment data if available
      let assignmentData: any = null;
      try {
        const response = await assignmentService.getProjectEquipmentByAssets(projectId!);
        if (response?.assets) {
          assignmentData = response;
        }
      } catch (assignmentError) {
        console.log('Assignment service not available, using equipment data only');
      }

      // Process equipment: use raw equipment as base, enrich with assignment data if available
      const enriched = rawEquipment.map((eq: any) => {
        const eqId = eq._id || eq.id;
        const eqEquipmentId = eq.equipmentId;
        const eqEquipmentName = eq.equipmentName || eq.name;

        // Find matching assignment data from assignment service if available
        let assignmentInfo: any = null;
        if (assignmentData?.assets) {
          for (const asset of assignmentData.assets) {
            const matchedEq = asset.equipment.find((a: any) =>
              (a._id || a.id) === eqId ||
              a.equipmentId === eqEquipmentId ||
              a.equipmentName === eqEquipmentName
            );
            if (matchedEq) {
              assignmentInfo = {
                assetName: asset.name,
                checkpointCount: matchedEq.checkpointCount || 0,
                isAssigned: matchedEq.isAssigned || false,
                assignedTo: matchedEq.assignedTo || null
              };
              break;
            }
          }
        }

        // Also check existingAssignments for assignment data (fallback/backup)
        if (!assignmentInfo || !assignmentInfo.isAssigned) {
          const matchingAssignment = existingAssignments.find((assignment: any) => {
            if (!assignment.isActive || assignment.assignmentType !== 'equipment') {
              return false;
            }
            const assignmentTargetId = assignment.targetId || assignment.equipmentId;
            const normalizedTargetId = normalizeId(assignmentTargetId);
            const normalizedEqId = normalizeId(eqId);
            const normalizedEqEquipmentId = normalizeId(eqEquipmentId);

            return (
              normalizedTargetId === normalizedEqId ||
              normalizedTargetId === normalizedEqEquipmentId ||
              assignmentTargetId === eqEquipmentId ||
              assignmentTargetId === eqEquipmentName
            );
          });

          if (matchingAssignment && matchingAssignment.isActive) {
            // Find assignee details from availableAssignees
            const assigneeId = matchingAssignment.assigneeId || (matchingAssignment as any).currentAssigneeId;
            const assignee = availableAssignees.find((a: any) =>
              normalizeId(a._id) === normalizeId(assigneeId)
            );

            if (assignee) {
              assignmentInfo = {
                ...(assignmentInfo || {}),
                isAssigned: true,
                assignedTo: {
                  name: assignee.name || matchingAssignment.assigneeName,
                  role: matchingAssignment.assigneeRole || assignee.role?.name || 'Site Engineer',
                  id: assignee._id,
                  _id: assignee._id
                }
              };
            } else if (matchingAssignment.assigneeName) {
              // Fallback: use assignment data directly if assignee not found in availableAssignees
              assignmentInfo = {
                ...(assignmentInfo || {}),
                isAssigned: true,
                assignedTo: {
                  name: matchingAssignment.assigneeName,
                  role: matchingAssignment.assigneeRole || 'Site Engineer',
                  id: matchingAssignment.assigneeId,
                  _id: matchingAssignment.assigneeId
                }
              };
            }
          }
        }

        // If still not assigned, check direct fields on equipment (siteEngineerId/siteSupervisorId)
        // This handles cases where assignment service might not return the status but the equipment itself has the reference
        if ((!assignmentInfo || !assignmentInfo.isAssigned) && (eq.siteEngineerId || eq.siteSupervisorId)) {
          // Prioritize Site Engineer for the main assignment display
          if (eq.siteEngineerId) {
            // Look up engineer name from availableAssignees if name field is empty
            let engineerName = eq.siteEngineerName;
            if (!engineerName && availableAssignees && Array.isArray(availableAssignees)) {
              const engineer = availableAssignees.find(a =>
                normalizeId(a._id) === normalizeId(eq.siteEngineerId)
              );
              if (engineer) {
                engineerName = engineer.name;
              }
            }

            assignmentInfo = {
              ...(assignmentInfo || {}),
              isAssigned: true,
              assignedTo: {
                name: engineerName || 'Unknown Engineer',
                role: 'Site Engineer',
                id: eq.siteEngineerId,
                _id: eq.siteEngineerId
              }
            };
          } else if (eq.siteSupervisorId) {
            // Look up supervisor name from availableAssignees if name field is empty
            let supervisorName = eq.siteSupervisorName;
            if (!supervisorName && availableAssignees && Array.isArray(availableAssignees)) {
              const supervisor = availableAssignees.find(a =>
                normalizeId(a._id) === normalizeId(eq.siteSupervisorId)
              );
              if (supervisor) {
                supervisorName = supervisor.name;
              }
            }

            assignmentInfo = {
              ...(assignmentInfo || {}),
              isAssigned: true,
              assignedTo: {
                name: supervisorName || 'Unknown Supervisor',
                role: 'Site Supervisor',
                id: eq.siteSupervisorId,
                _id: eq.siteSupervisorId
              }
            };
          }
        }

        // Extract zone and sublocation - prioritize direct fields, then parse from name
        const zone = eq.zone || eq.block || (() => {
          const parsed = parseEquipmentName(eq.equipmentName || eq.equipmentId || '');
          return parsed?.zone || 'Unknown Zone';
        })();

        const subLocation = eq.subLocation || (() => {
          const parsed = parseEquipmentName(eq.equipmentName || eq.equipmentId || '');
          return parsed?.subLocation || 'No SubLocation';
        })();

        return {
          ...eq,
          // Zone and sublocation
          zone,
          subLocation,
          // Manufacturer and model - use direct fields
          manufacturer: eq.manufacturer || eq.manufacturerName || undefined,
          manufacturerName: eq.manufacturerName || eq.manufacturer || undefined,
          makeAndModel: eq.makeAndModel || eq.makeAndModelName || undefined,
          makeAndModelName: eq.makeAndModelName || eq.makeAndModel || undefined,
          // Assignment info - prioritize assignment service, fallback to existingAssignments
          ...(assignmentInfo ? {
            assetName: assignmentInfo.assetName || eq.assetName,
            checkpointCount: assignmentInfo.checkpointCount || 0,
            isAssigned: assignmentInfo.isAssigned || false,
            assignedTo: assignmentInfo.assignedTo || null
          } : {
            assetName: eq.assetName,
            checkpointCount: 0,
            isAssigned: false,
            assignedTo: null
          })
        };
      });

      setEquipment(enriched);
      console.log('Equipment loaded:', enriched.length, 'items organized by zone/sublocation');
      console.log('Equipment zones:', [...new Set(enriched.map((e: any) => e.zone))]);
      console.log('Equipment sublocations:', [...new Set(enriched.map((e: any) => e.subLocation))]);

      // Debug: Log assigned equipment
      const assignedEquipment = enriched.filter((e: any) => e.isAssigned);
      console.log('Assigned equipment count:', assignedEquipment.length);
      assignedEquipment.forEach((eq: any) => {
        console.log(`Equipment ${eq.equipmentName || eq.equipmentId}: assigned to ${eq.assignedTo?.name || 'Unknown'} (${eq.assignedTo?.role || 'Unknown'})`);
      });
    } catch (error: any) {
      console.error('Error loading equipment:', error);
      toast.error(error.message || 'Failed to load equipment');
    } finally {
      setEquipmentLoading(false);
    }
  };

  const loadAvailableAssignees = async () => {
    try {
      setAssigneesLoading(true);

      // Pass the projectId to the backend to filter by project-assigned resources
      const queryParams: any = {};


      if (projectId) {
        queryParams.projectId = projectId;
      }

      console.log('Loading assignees with params:', queryParams);

      const response = await assignmentService.getEngineersWithAssignments(queryParams);
      const engineers = response?.engineers || [];

      setAvailableAssignees(engineers);

      console.log('Engineers loaded from backend:', engineers);
      console.log('Available Engineers:', engineers.filter((e: any) => e.role?.name === 'Site Engineer'));
      console.log('Available Supervisors:', engineers.filter((e: any) => e.role?.name === 'Site Supervisor'));

    } catch (error: any) {
      console.error('Failed to load assignees:', error);
      // Set empty array on error to prevent runtime issues
      // This handles cases where the assignment service endpoints are not available (404 errors)
      setAvailableAssignees([]);
      // Only show toast for non-404 errors (assignment service not available)
      if (error.response?.status !== 404) {
        toast.error(error.message || 'Failed to load assignees');
      }
    } finally {
      setAssigneesLoading(false);
    }
  };

  const loadExistingAssignments = async () => {
    try {
      const response = await assignmentService.getAssignments({ projectId });
      setExistingAssignments(response?.data || []);
    } catch (error: any) {
      console.error('Failed to load assignments:', error);
      // Set empty array on error to prevent runtime issues
      // This handles cases where the assignment service endpoints are not available (404 errors)
      setExistingAssignments([]);
    }
  };

  // Removed loadEngineerWorkload function - focusing only on project equipment assignments








  // Helper function to get assigned equipment count
  const getAssignedEquipmentCount = (assigneeId: string) => {
    return equipment.filter(eq => {
      const assignedId = normalizeId(eq?.assignedTo?._id) || normalizeId(eq?.assignedTo?.id);
      const engineerId = normalizeId(eq?.siteEngineerId);
      const supervisorId = normalizeId(eq?.siteSupervisorId);
      return assignedId === assigneeId || engineerId === assigneeId || supervisorId === assigneeId;
    }).length;
  };

  // ==========================================
  // HANDLERS
  // ==========================================
  const handleSelectMember = async (member: Assignee) => {
    setSelectedMember(member);
    setShowMemberDetails(true);
    setResourceWorkload(null);
    setIsWorkloadLoading(true);

    try {
      if (member._id) {
        const workload = await assignmentService.getResourceWorkload(member._id);
        setResourceWorkload(workload);
      }
    } catch (error) {
      console.error('Failed to fetch resource workload:', error);
    } finally {
      setIsWorkloadLoading(false);
    }
  };

  const handleCloseMemberDetails = () => {
    setShowMemberDetails(false);
    setTimeout(() => {
      setSelectedMember(null);
      setResourceWorkload(null);
    }, 300);
  };


  // Helper function to close assignment details
  const closeAssignmentDetails = () => {
    setShowAssignmentDetails(false);
    setSelectedMemberAssignments([]);
  };



  // Handle individual equipment selection
  const handleEquipmentSelect = (equipmentId: string, isSelected: boolean) => {
    if (isSelected) {
      setSelectedEquipmentIds(prev => [...prev, equipmentId]);
    } else {
      setSelectedEquipmentIds(prev => prev.filter(id => id !== equipmentId));
    }

    // Update asset selection status based on individual equipment selection
    const equipmentItem = equipment.find(e => (e._id || e.id) === equipmentId);
    if (equipmentItem) {
      // Asset selection logic can be added here if needed in the future
    }
  };

  // Handle select all equipment
  const handleSelectAllEquipment = (isSelected: boolean) => {
    if (isSelected) {
      const allEquipmentIds = equipment.map(e => e._id || e.id);
      setSelectedEquipmentIds(allEquipmentIds);
    } else {
      setSelectedEquipmentIds([]);
    }
  };

  // Handle asset-wise select all
  const handleSelectAllAssetEquipment = (assetName: string, isSelected: boolean) => {
    const assetEquipment = equipment.filter(e => (e.assetName || 'No Asset') === assetName);
    const assetEquipmentIds = assetEquipment.map(e => e._id || e.id);

    if (isSelected) {
      // Add all equipment in this asset to selection
      setSelectedEquipmentIds(prev => {
        const newSelection = [...prev];
        assetEquipmentIds.forEach(id => {
          if (!newSelection.includes(id)) {
            newSelection.push(id);
          }
        });
        return newSelection;
      });
    } else {
      // Remove all equipment in this asset from selection
      setSelectedEquipmentIds(prev => prev.filter(id => !assetEquipmentIds.includes(id)));
    }
  };


  // Check if all equipment is selected
  const isAllEquipmentSelected = useMemo(() => {
    if (!equipment || equipment.length === 0) return false;
    const allEquipmentIds = equipment.map(e => e._id || e.id);
    return allEquipmentIds.every(id => selectedEquipmentIds.includes(id));
  }, [equipment, selectedEquipmentIds]);

  // Check if some equipment is selected (for indeterminate state)
  const isSomeEquipmentSelected = useMemo(() => {
    return selectedEquipmentIds.length > 0 && !isAllEquipmentSelected;
  }, [selectedEquipmentIds, isAllEquipmentSelected]);

  // Check if all equipment in an asset is selected
  const isAllAssetEquipmentSelected = (assetName: string) => {
    const assetEquipment = equipment.filter(e => (e.assetName || 'No Asset') === assetName);
    if (assetEquipment.length === 0) return false;
    const assetEquipmentIds = assetEquipment.map(e => e._id || e.id);
    return assetEquipmentIds.every(id => selectedEquipmentIds.includes(id));
  };

  // Check if some equipment in an asset is selected (for indeterminate state)
  const isSomeAssetEquipmentSelected = (assetName: string) => {
    const assetEquipment = equipment.filter(e => (e.assetName || 'No Asset') === assetName);
    if (assetEquipment.length === 0) return false;
    const assetEquipmentIds = assetEquipment.map(e => e._id || e.id);
    const selectedCount = assetEquipmentIds.filter(id => selectedEquipmentIds.includes(id)).length;
    return selectedCount > 0 && selectedCount < assetEquipmentIds.length;
  };

  // Helper function to get approval status from ProjectResourceApproval (source of truth)
  const getResourceApprovalStatus = (resourceId: string): {
    status: 'pending' | 'approved' | 'rejected' | 'modified';
    approverName?: string;
    approverEmail?: string;
    approverComments?: string;
    approvedAt?: Date;
    rejectedAt?: Date;
  } => {
    if (!resourceApprovalData) {
      return { status: 'pending' };
    }

    // Check in hierarchy (supervisors and their engineers)
    for (const supervisor of resourceApprovalData.hierarchy || []) {
      if (normalizeId(supervisor._id) === normalizeId(resourceId)) {
        return {
          status: supervisor.approvalStatus || 'pending',
          approverName: (supervisor as any).approverName,
          approverEmail: (supervisor as any).approverEmail,
          approverComments: supervisor.approverComments,
          approvedAt: supervisor.approvedAt,
          rejectedAt: supervisor.rejectedAt
        };
      }
      // Check engineers under this supervisor
      for (const engineer of supervisor.subordinates || []) {
        if (normalizeId(engineer._id) === normalizeId(resourceId)) {
          return {
            status: engineer.approvalStatus || 'pending',
            approverName: (engineer as any).approverName,
            approverEmail: (engineer as any).approverEmail,
            approverComments: engineer.approverComments,
            approvedAt: engineer.approvedAt,
            rejectedAt: engineer.rejectedAt
          };
        }
      }
    }

    // Check independent engineers
    for (const engineer of resourceApprovalData.independentEngineers || []) {
      if (normalizeId(engineer._id) === normalizeId(resourceId)) {
        return {
          status: engineer.approvalStatus || 'pending',
          approverName: (engineer as any).approverName,
          approverEmail: (engineer as any).approverEmail,
          approverComments: engineer.approverComments,
          approvedAt: engineer.approvedAt,
          rejectedAt: engineer.rejectedAt
        };
      }
    }

    return { status: 'pending' };
  };

  const resourceAssignmentSummary = useMemo(() => {
    const summary: Record<string, ResourceInsight> = {};

    const ensureInsight = (id: string) => {
      if (!summary[id]) {
        summary[id] = {
          total: 0,
          approved: 0,
          rejected: 0,
          pending: 0,
          supervisors: new Set<string>(),
          engineers: new Set<string>(),
        };
      }
      return summary[id];
    };

    // Group assignments by resource (assigneeId) to count equipment
    const assignmentsByResource = new Map<string, any[]>();
    existingAssignments.forEach((assignment: any) => {
      const assigneeId = normalizeId(assignment.currentAssigneeId || assignment.assigneeId);
      if (assigneeId) {
        if (!assignmentsByResource.has(assigneeId)) {
          assignmentsByResource.set(assigneeId, []);
        }
        assignmentsByResource.get(assigneeId)!.push(assignment);
      }
    });

    // Process equipment to count total assignments per resource and relationships
    equipment.forEach((eq) => {
      const engineerId = normalizeId(eq?.siteEngineerId) || (eq?.assignedTo?.role === 'Site Engineer' ? normalizeId(eq?.assignedTo?.id) : null);
      const supervisorId = normalizeId(eq?.siteSupervisorId) || (eq?.assignedTo?.role === 'Site Supervisor' ? normalizeId(eq?.assignedTo?.id) : null);

      if (engineerId) {
        const insight = ensureInsight(engineerId);
        insight.total += 1;
        if (supervisorId) {
          insight.supervisors.add(supervisorId);
        }
      }

      if (supervisorId) {
        const insight = ensureInsight(supervisorId);
        insight.total += 1;
        if (engineerId) {
          insight.engineers.add(engineerId);
        }
      }
    });

    // Now update approval status based on ProjectResourceApproval (source of truth) - matching ProjectApprovalDrawer logic
    // Use ProjectResourceApproval status as the source of truth for EVERY resource present in summary,
    // regardless of whether legacy assignments exist or not.
    Object.keys(summary).forEach((resourceId) => {
      const insight = ensureInsight(resourceId);
      const approvalInfo = getResourceApprovalStatus(resourceId);
      const approvalStatus = approvalInfo.status;

      // Reset counters
      insight.approved = 0;
      insight.rejected = 0;
      insight.pending = 0;

      // Apply counts based on resource-level approval status
      if (approvalStatus === 'approved') {
        insight.approved = insight.total;
      } else if (approvalStatus === 'rejected') {
        insight.rejected = insight.total;
      } else if (approvalStatus === 'modified') {
        insight.pending = insight.total; // Treat modified as pending for counts
      } else {
        insight.pending = insight.total;
      }
    });

    return summary;
  }, [equipment, existingAssignments, resourceApprovalData]);

  const getReviewStatusChip = (insight?: ResourceInsight, resourceId?: string) => {
    const projectStatus = (project?.approvalStatus || '').toLowerCase();
    const inReviewStatuses = ['pending_approval', 'in_review', 'under_review', 'approved', 'partially_approved', 'rejected'];
    const isProjectInReview = inReviewStatuses.includes(projectStatus);

    if (!isProjectInReview) {
      return {
        label: 'Not submitted for review',
        className: 'bg-gray-100 text-gray-600 border border-gray-200',
        approverInfo: null
      };
    }

    // Get approval info from ProjectResourceApproval (source of truth)
    const approvalInfo = resourceId ? getResourceApprovalStatus(resourceId) : null;

    // If no insight derived from assignments/equipment, fall back to API approval status
    if (!insight) {
      if (approvalInfo?.status === 'approved') {
        return {
          label: approvalInfo?.approverName
            ? `Review: Approved by ${approvalInfo.approverName}`
            : 'Review: Approved',
          className: 'bg-green-50 text-green-700 border border-green-200',
          approverInfo: approvalInfo || null
        };
      }
      if (approvalInfo?.status === 'rejected') {
        return {
          label: approvalInfo?.approverName
            ? `Review: Rejected by ${approvalInfo.approverName}`
            : 'Review: Rejected',
          className: 'bg-red-50 text-red-700 border border-red-200',
          approverInfo: approvalInfo || null
        };
      }
      if (approvalInfo?.status === 'modified') {
        return {
          label: 'Review: Modified (Pending)',
          className: 'bg-blue-50 text-blue-700 border border-blue-200',
          approverInfo: approvalInfo || null
        };
      }
      return {
        label: 'Review pending',
        className: 'bg-gray-100 text-gray-600 border border-gray-200',
        approverInfo: null
      };
    }

    // Matching ProjectApprovalDrawer logic:
    // - Rejected if ANY assignment is rejected
    // - Approved if ALL assignments are approved AND none pending
    // - Pending otherwise

    if (insight.rejected > 0) {
      return {
        label: approvalInfo?.approverName
          ? `Review: Rejected by ${approvalInfo.approverName}`
          : 'Review: Rejected',
        className: 'bg-red-50 text-red-700 border border-red-200',
        approverInfo: approvalInfo || null
      };
    }

    // Approved: all assignments approved and no pending/rejected
    if (insight.approved > 0 && insight.pending === 0 && insight.rejected === 0) {
      return {
        label: approvalInfo?.approverName
          ? `Review: Approved by ${approvalInfo.approverName}`
          : 'Review: Approved',
        className: 'bg-green-50 text-green-700 border border-green-200',
        approverInfo: approvalInfo || null
      };
    }

    // Pending: has pending assignments or no assignments found
    return {
      label: isProjectInReview ? 'Review pending' : 'Not submitted for review',
      className: isProjectInReview
        ? 'bg-amber-50 text-amber-700 border border-amber-200'
        : 'bg-gray-100 text-gray-600 border border-gray-200',
      approverInfo: null
    };
  };

  // Hierarchical assignees from API (supervisors with subordinates and independent engineers)
  // Merged with availableAssignees to ensure all resources are shown even if not in approval data
  const hierarchicalAssignees = useMemo(() => {
    const supervisors: any[] = resourceApprovalData?.hierarchy ? [...resourceApprovalData.hierarchy] : [];
    const independentEngineers: any[] = resourceApprovalData?.independentEngineers ? [...resourceApprovalData.independentEngineers] : [];

    // Helper to check if resource exists in current lists
    const isSupervisorExists = (id: string) => supervisors.some(s => normalizeId(s._id) === normalizeId(id));
    const isEngineerExists = (id: string) => {
      // Check in independent engineers
      if (independentEngineers.some(e => normalizeId(e._id) === normalizeId(id))) return true;
      // Check in supervisors' subordinates
      return supervisors.some(s => s.subordinates?.some((sub: any) => normalizeId(sub._id) === normalizeId(id)));
    };

    // Merge from availableAssignees
    if (availableAssignees) {
      availableAssignees.forEach(assignee => {
        const id = normalizeId(assignee._id);
        if (!id) return;

        if (assignee.role?.name === 'Site Supervisor') {
          if (!isSupervisorExists(id)) {
            supervisors.push({
              ...assignee,
              subordinates: [], // Initialize with empty subordinates
              approvalStatus: 'pending' // Default status
            });
          }
        } else if (assignee.role?.name === 'Site Engineer') {
          if (!isEngineerExists(id)) {
            independentEngineers.push({
              ...assignee,
              approvalStatus: 'pending'
            });
          }
        }
      });
    }

    return {
      supervisors,
      independentEngineers
    };
  }, [resourceApprovalData, availableAssignees]);

  // Check if project is ready to start (all resources approved)
  const isProjectReadyToStart = useMemo(() => {
    if (!project) return false;

    // Check project overall approval status
    const projectApprovalStatus = (project.approvalStatus || 'pending').toLowerCase();
    if (projectApprovalStatus === 'approved') {
      return true;
    }

    // Check if all resources are approved
    const allResources = [
      ...hierarchicalAssignees.supervisors,
      ...hierarchicalAssignees.independentEngineers
    ];

    if (allResources.length === 0) return false;

    return allResources.every(resource => {
      const insight = resourceAssignmentSummary[resource._id];
      return insight && insight.approved > 0 && insight.pending === 0 && insight.rejected === 0;
    });
  }, [project, hierarchicalAssignees, resourceAssignmentSummary]);

  // Check if equipment is assigned
  const isEquipmentAssigned = (equipmentId: string) => {
    // First check if equipment has isAssigned property (from enriched data)
    const equipmentItem = equipment?.find(e => {
      const eqId = normalizeId(e._id || e.id);
      const targetId = normalizeId(equipmentId);
      return eqId === targetId ||
        normalizeId(e.equipmentId) === targetId ||
        e.equipmentName === equipmentId ||
        e.equipmentId === equipmentId;
    });

    if (equipmentItem) {
      if (typeof equipmentItem.isAssigned === 'boolean') {
        return equipmentItem.isAssigned;
      }
      // Also check if assignedTo exists
      if (equipmentItem.assignedTo) {
        return true;
      }
    }

    // Fallback to checking existingAssignments (legacy approach)
    if (!existingAssignments || !Array.isArray(existingAssignments)) {
      return false;
    }

    const targetId = normalizeId(equipmentId);
    return existingAssignments.some(assignment => {
      if (!assignment.isActive || assignment.assignmentType !== 'equipment') {
        return false;
      }
      const assignmentTargetId = normalizeId(assignment.targetId || (assignment as any).equipmentId);
      return assignmentTargetId === targetId;
    });
  };

  // Get assignee for equipment
  const getEquipmentAssignee = (equipmentId: string) => {
    // First check if equipment has assignedTo property (from enriched data)
    const equipmentItem = equipment?.find(e => {
      const eqId = normalizeId(e._id || e.id);
      const targetId = normalizeId(equipmentId);
      return eqId === targetId ||
        normalizeId(e.equipmentId) === targetId ||
        e.equipmentName === equipmentId ||
        e.equipmentId === equipmentId;
    });

    if (equipmentItem && equipmentItem.assignedTo) {
      return {
        assigneeName: equipmentItem.assignedTo.name,
        assigneeRole: equipmentItem.assignedTo.role,
        assigneeId: equipmentItem.assignedTo.id || equipmentItem.assignedTo._id,
        targetId: equipmentId,
        isActive: true
      };
    }

    // Fallback to checking existingAssignments (legacy approach)
    if (!existingAssignments || !Array.isArray(existingAssignments)) {
      return undefined;
    }

    const matchingAssignment = existingAssignments.find(assignment => {
      if (!assignment.isActive || assignment.assignmentType !== 'equipment') {
        return false;
      }
      const assignmentTargetId = normalizeId(assignment.targetId || (assignment as any).equipmentId);
      const targetId = normalizeId(equipmentId);
      return assignmentTargetId === targetId;
    });

    if (matchingAssignment) {
      // Find assignee details
      const assigneeId = matchingAssignment.assigneeId || (matchingAssignment as any).currentAssigneeId;
      const assignee = availableAssignees.find((a: any) =>
        normalizeId(a._id) === normalizeId(assigneeId)
      );

      if (assignee) {
        return {
          assigneeName: assignee.name || matchingAssignment.assigneeName,
          assigneeRole: matchingAssignment.assigneeRole || assignee.role?.name || 'Site Engineer',
          assigneeId: assignee._id,
          targetId: equipmentId,
          isActive: true
        };
      }

      // Fallback to assignment data
      return {
        assigneeName: matchingAssignment.assigneeName,
        assigneeRole: matchingAssignment.assigneeRole || 'Site Engineer',
        assigneeId: matchingAssignment.assigneeId,
        targetId: equipmentId,
        isActive: true
      };
    }

    return undefined;
  };

  // Color coding for resources - Generate unique dot colors for each engineer
  const getEngineerDotColor = (assigneeId: string) => {
    if (!assigneeId) return 'bg-gray-400';

    // Generate a consistent dot color based on assigneeId
    const dotColors = [
      'bg-blue-500',      // Blue
      'bg-green-500',     // Green
      'bg-purple-500',    // Purple
      'bg-orange-500',    // Orange
      'bg-pink-500',      // Pink
      'bg-indigo-500',    // Indigo
      'bg-cyan-500',      // Cyan
      'bg-yellow-500',    // Yellow
      'bg-red-500',       // Red
      'bg-teal-500',      // Teal
      'bg-lime-500',      // Lime
      'bg-amber-500',     // Amber
      'bg-emerald-500',   // Emerald
      'bg-violet-500',    // Violet
      'bg-rose-500',      // Rose
      'bg-sky-500'        // Sky
    ];

    // Create a simple hash from assigneeId to get consistent color
    let hash = 0;
    for (let i = 0; i < assigneeId.length; i++) {
      hash = assigneeId.charCodeAt(i) + ((hash << 5) - hash);
    }
    const colorIndex = Math.abs(hash) % dotColors.length;
    return dotColors[colorIndex];
  };

  // Get engineer badge color (lighter version for badges)
  const getEngineerBadgeColor = (assigneeId: string) => {
    if (!assigneeId) return 'bg-gray-100 text-gray-700';

    const badgeColors = [
      'bg-blue-100 text-blue-700 border-blue-300',
      'bg-green-100 text-green-700 border-green-300',
      'bg-purple-100 text-purple-700 border-purple-300',
      'bg-orange-100 text-orange-700 border-orange-300',
      'bg-pink-100 text-pink-700 border-pink-300',
      'bg-indigo-100 text-indigo-700 border-indigo-300',
      'bg-cyan-100 text-cyan-700 border-cyan-300',
      'bg-yellow-100 text-yellow-700 border-yellow-300',
      'bg-red-100 text-red-700 border-red-300',
      'bg-teal-100 text-teal-700 border-teal-300',
      'bg-lime-100 text-lime-700 border-lime-300',
      'bg-amber-100 text-amber-700 border-amber-300',
      'bg-emerald-100 text-emerald-700 border-emerald-300',
      'bg-violet-100 text-violet-700 border-violet-300',
      'bg-rose-100 text-rose-700 border-rose-300',
      'bg-sky-100 text-sky-700 border-sky-300'
    ];

    let hash = 0;
    for (let i = 0; i < assigneeId.length; i++) {
      hash = assigneeId.charCodeAt(i) + ((hash << 5) - hash);
    }
    const colorIndex = Math.abs(hash) % badgeColors.length;
    return badgeColors[colorIndex];
  };

  // Legacy function for backward compatibility
  const getResourceColor = (assigneeId: string) => {
    return getEngineerBadgeColor(assigneeId);
  };

  // Get resource highlight color for selected resource
  const getResourceHighlightColor = (assigneeId: string) => {
    if (!selectedResourceId) return '';
    return assigneeId === selectedResourceId ? 'ring-2 ring-blue-500 ring-opacity-70' : '';
  };

  // Get resource name by ID
  const getResourceName = (assigneeId: string) => {
    const assignee = availableAssignees.find(a => a._id === assigneeId);
    return assignee ? assignee.name : 'Unknown';
  };



  // Removed equipment checkpoint view handler - focusing only on project equipment assignments



  // New state for assignment drawer
  const [showAssignmentDrawer, setShowAssignmentDrawer] = useState(false);
  const [assignmentMode, setAssignmentMode] = useState<'engineer' | 'supervisor'>('engineer');
  const [assignmentFormData, setAssignmentFormData] = useState({
    assigneeId: '',
    assigneeRole: 'Site Engineer' as 'Site Engineer' | 'Site Supervisor',
    supervisorId: ''
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reassignment confirmation state
  const [showReassignmentConfirm, setShowReassignmentConfirm] = useState(false);

  // Removed engineer workload loading - focusing only on project equipment assignments
  const [reassignmentData, setReassignmentData] = useState<{
    alreadyAssignedEquipment: any[];
    newAssignee: any;
    assignmentData: any;
  } | null>(null);



  // Helper function to check for already assigned equipment
  const getAlreadyAssignedEquipment = (selectedEquipmentIds: string[]) => {
    return selectedEquipmentIds
      .map(id => equipment.find(eq => (eq._id || eq.id) === id))
      .filter(eq => eq && (eq.isAssigned || isEquipmentAssigned(eq._id || eq.id)))
      .map(eq => ({
        ...eq,
        currentAssignee: eq.assignedTo || getEquipmentAssignee(eq._id || eq.id)
      }));
  };

  // Validation function
  const validateForm = () => {
    const errors: Record<string, string> = {};

    if (!assignmentFormData.assigneeId) {
      if (assignmentMode === 'engineer') {
        errors.assigneeId = 'Please select a site engineer';
      } else {
        errors.assigneeId = 'Please select a site supervisor';
      }
    }

    // Only require supervisor selection when assigning to engineer
    if (assignmentMode === 'engineer' && !assignmentFormData.supervisorId) {
      errors.supervisorId = 'Please select a site supervisor';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submission
  const handleAssignmentSubmit = async () => {
    // Check if project is already active/approved
    if (project?.approvalStatus === 'approved' || project?.approvalStatus === 'in_progress') {
      if (!window.confirm('Project is already active. Changing assignments will require re-approval. Do you want to continue?')) {
        return;
      }
    }

    if (!validateForm()) {
      return;
    }

    if (selectedEquipmentIds.length === 0) {
      toast.error('Please select at least one equipment');
      return;
    }

    const assignee = availableAssignees.find(a => a._id === assignmentFormData.assigneeId);
    if (!assignee) {
      toast.error('Selected assignee not found');
      return;
    }

    // Verify assignee role matches assignment mode
    const expectedRole = assignmentMode === 'engineer' ? 'Site Engineer' : 'Site Supervisor';
    const actualRole = assignee.role?.name || assignee.role;
    if (actualRole !== expectedRole) {
      toast.error(`Selected assignee is a ${actualRole}, but you're assigning to ${expectedRole}`);
      return;
    }

    // Check for already assigned equipment
    const alreadyAssignedEquipment = getAlreadyAssignedEquipment(selectedEquipmentIds);

    if (alreadyAssignedEquipment.length > 0) {
      // Show reassignment confirmation
      const assigneeRole = assignmentMode === 'engineer' ? 'Site Engineer' : 'Site Supervisor';

      // Build assignment data - ensure supervisorId is only included for engineers
      const assignmentData: any = {
        projectId: projectId!,
        assignmentType: 'equipment' as 'equipment',
        targetIds: selectedEquipmentIds,
        assigneeId: assignmentFormData.assigneeId,
        assigneeRole: assigneeRole as 'Site Engineer' | 'Site Supervisor',
        overwriteExisting: true
      };

      // Only include supervisorId when assigning to an engineer
      if (assignmentMode === 'engineer' && assignmentFormData.supervisorId) {
        assignmentData.supervisorId = assignmentFormData.supervisorId;
      }

      setReassignmentData({
        alreadyAssignedEquipment,
        newAssignee: assignee,
        assignmentData
      });
      setShowReassignmentConfirm(true);
      return;
    }

    // Proceed with assignment if no reassignments needed
    await proceedWithAssignment(assignee);
  };

  // Proceed with assignment (called after confirmation or when no reassignments)
  const proceedWithAssignment = async (assignee: any) => {
    setIsSubmitting(true);

    try {
      // Determine assignee role based on assignment mode
      const assigneeRole = assignmentMode === 'engineer' ? 'Site Engineer' : 'Site Supervisor';

      // Build assignment data - ensure supervisorId is only included for engineers
      const assignmentData: any = {
        projectId: projectId!,
        assignmentType: 'equipment' as 'equipment',
        targetIds: selectedEquipmentIds,
        assigneeId: assignmentFormData.assigneeId,
        assigneeRole: assigneeRole as 'Site Engineer' | 'Site Supervisor',
        overwriteExisting: true
      };

      // Only include supervisorId when assigning to an engineer
      if (assignmentMode === 'engineer' && assignmentFormData.supervisorId) {
        assignmentData.supervisorId = assignmentFormData.supervisorId;
      }
      // For supervisors, explicitly do not include supervisorId

      console.log('Creating assignment with data:', assignmentData);
      console.log('Assignment mode:', assignmentMode);
      console.log('Assignee:', assignee);

      await assignmentService.createBulkAssignment(assignmentData);
      emitProjectProgressUpdate(projectId, 'resource-assignment');

      const equipmentCount = selectedEquipmentIds.length;

      let successMessage = `Successfully assigned ${equipmentCount} equipment to ${assignee.name}`;

      if (assignmentMode === 'engineer') {
        const supervisor = availableSupervisors.find(s => s._id === assignmentFormData.supervisorId);
        successMessage += ` (${supervisor?.name})`;
      }

      toast.success(successMessage);

      // Reset form and close drawer
      setAssignmentFormData({
        assigneeId: '',
        assigneeRole: 'Site Engineer',
        supervisorId: ''
      });
      setFormErrors({});
      setSelectedEquipmentIds([]);
      setShowAssignmentDrawer(false);

      // Reload all data to reflect the latest changes
      // Refresh in proper order to ensure team hierarchy and assignment structure updates correctly

      // Step 1: Refresh resource approval data and available assignees in parallel
      // (These are independent and can be loaded simultaneously)
      await Promise.all([
        loadResourceApprovalData(), // Contains hierarchy structure
        loadAvailableAssignees()     // Needed for hierarchy merge
      ]);

      // Step 2: Refresh assignments (needed for equipment assignment status)
      await loadExistingAssignments();

      // Step 3: Refresh equipment (uses assignment data and hierarchy)
      // This must come last as it depends on assignments and hierarchy data
      await loadProjectEquipment();

      // Force a small delay to ensure all state updates propagate and UI re-renders
      await new Promise(resolve => setTimeout(resolve, 150));

      // Show refresh confirmation
      toast.info('Assignment structure and team hierarchy refreshed');

    } catch (error: any) {
      toast.error(error.message || 'Failed to create assignments');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle reassignment confirmation
  const handleReassignmentConfirm = async () => {
    if (!reassignmentData) return;

    setShowReassignmentConfirm(false);
    await proceedWithAssignment(reassignmentData.newAssignee);
    setReassignmentData(null);
  };

  // Handle reassignment cancellation
  const handleReassignmentCancel = () => {
    setShowReassignmentConfirm(false);
    setReassignmentData(null);
  };

  // Get available supervisors
  const availableSupervisors = useMemo(() => {
    if (!availableAssignees || !Array.isArray(availableAssignees)) {
      console.log('No available assignees for supervisors:', availableAssignees);
      return [];
    }
    const supervisors = availableAssignees.filter(assignee => assignee.role.name === 'Site Supervisor');
    console.log('Filtered supervisors:', supervisors);
    return supervisors;
  }, [availableAssignees]);

  // Get available engineers
  const availableEngineers = useMemo(() => {
    if (!availableAssignees || !Array.isArray(availableAssignees)) {
      console.log('No available assignees for engineers:', availableAssignees);
      return [];
    }
    const engineers = availableAssignees.filter(assignee => assignee.role.name === 'Site Engineer');
    console.log('Filtered engineers:', engineers);
    return engineers;
  }, [availableAssignees]);

  // Auto-select assignee if only 1 option available
  useEffect(() => {
    if (showAssignmentDrawer && !assignmentFormData.assigneeId) {
      const options = assignmentMode === 'engineer' ? availableEngineers : availableSupervisors;
      if (options.length === 1) {
        setAssignmentFormData(prev => ({
          ...prev,
          assigneeId: options[0]._id
        }));
      }
    }
  }, [showAssignmentDrawer, assignmentMode, availableEngineers, availableSupervisors, assignmentFormData.assigneeId]);

  // Auto-select supervisor if only 1 option available (when assigning to engineer)
  // This runs after assignee is selected or when drawer opens with engineer mode
  useEffect(() => {
    if (showAssignmentDrawer && assignmentMode === 'engineer' && !assignmentFormData.supervisorId) {
      if (availableSupervisors.length === 1) {
        setAssignmentFormData(prev => ({
          ...prev,
          supervisorId: availableSupervisors[0]._id
        }));
      }
    }
  }, [showAssignmentDrawer, assignmentMode, assignmentFormData.supervisorId, availableSupervisors]);

  if (loading || !project) {
    return (
      <RightDrawer
        open={isOpen}
        onClose={handleClose}
        title="Project Assignments"
        size="full"
      >
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-gray-500" />
            <p className="text-gray-600">Loading project...</p>
          </div>
        </div>
      </RightDrawer>
    );
  }

  return (
    <RightDrawer
      open={isOpen}
      onClose={handleClose}
      title="Project Assignments"
      size="full"
    >
      <div className="min-h-screen bg-gray-50/50 font-sans">
        {/* Progress Header */}
        {project && (
          <div className="sticky top-0 z-30 bg-white/90 backdrop-blur-xl border-b border-gray-200/50 shadow-sm transition-all duration-300">
            <ProjectProgressHeader
              project={project}
              onRefresh={handleRefreshAssignments}
            />
          </div>
        )}

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="p-6 max-w-[1920px] mx-auto space-y-8"
        >
          {/* Page Header & Controls */}
          <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 tracking-tight flex items-center gap-3">
                Assignment Overview
                <span className="px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 text-sm font-medium border border-emerald-200">
                  Live
                </span>
              </h1>
              <p className="text-gray-500 mt-2 flex items-center gap-2 text-lg">
                <Building2 size={20} className="text-gray-400" />
                {project.projectName}
                <span className="text-gray-300 mx-2">|</span>
                <span className="font-mono text-gray-600 bg-gray-100 px-2 py-0.5 rounded">{project.projectId}</span>
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-4">
              {/* Global Selection Controls */}
              <div className="flex items-center gap-3 bg-white px-4 py-2.5 rounded-xl border border-gray-200 shadow-sm">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={isAllEquipmentSelected}
                    ref={(input) => {
                      if (input) input.indeterminate = isSomeEquipmentSelected;
                    }}
                    onChange={(e) => handleSelectAllEquipment(e.target.checked)}
                    className="w-5 h-5 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500 transition-all cursor-pointer"
                  />
                  <span className="text-sm font-semibold text-gray-700">
                    Select All ({equipment.length})
                  </span>
                </div>
                {selectedEquipmentIds.length > 0 && (
                  <>
                    <div className="w-px h-6 bg-gray-200 mx-2"></div>
                    <button
                      onClick={() => setSelectedEquipmentIds([])}
                      className="text-sm text-red-600 hover:text-red-700 font-medium hover:bg-red-50 px-2 py-1 rounded transition-colors"
                    >
                      Clear ({selectedEquipmentIds.length})
                    </button>
                  </>
                )}
              </div>

              {selectedEquipmentIds.length > 0 && (
                <motion.button
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    setAssignmentMode('engineer');
                    setAssignmentFormData({
                      assigneeId: '',
                      assigneeRole: 'Site Engineer',
                      supervisorId: ''
                    });
                    setFormErrors({});
                    setShowAssignmentDrawer(true);
                  }}
                  className="inline-flex items-center gap-2 px-6 py-2.5 text-white rounded-xl shadow-lg shadow-emerald-500/30 hover:shadow-emerald-500/40 transition-all duration-300 font-bold"
                  style={{ background: `linear-gradient(135deg, ${BRAND_COLORS.primary}, ${BRAND_COLORS.primaryHover})` }}
                >
                  <UserCheck size={20} />
                  Assign Selected
                </motion.button>
              )}

              <button
                onClick={handleRefreshAssignments}
                className="p-2.5 text-gray-500 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 hover:text-emerald-600 hover:border-emerald-200 transition-all shadow-sm"
                title="Refresh Data"
              >
                <RefreshCw size={20} />
              </button>
            </div>
          </div>

          {/* Main Content Grid - Split View Only */}
          <div className="space-y-8">
            {/* Split View: Architecture (Left 8/12) + Team Hierarchy (Right 4/12) */}
            <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
              {/* Left: Architecture View - 8 columns (66.67%) */}
              <div className="xl:col-span-8 h-[800px]">
                <ArchitectureAssignmentView
                  equipment={equipment}
                  selectedEquipmentIds={selectedEquipmentIds}
                  onSelectEquipment={handleEquipmentSelect}
                  onSelectAllAsset={handleSelectAllAssetEquipment}
                  onSelectMember={(member: any) => {
                    setSelectedMember(member);
                    setShowMemberDetails(true);
                  }}
                />
              </div>

              {/* Right: Team Hierarchy View - 4 columns (33.33%) */}
              <div className="xl:col-span-4 h-[800px]">
                <HierarchyGraph
                  supervisors={hierarchicalAssignees.supervisors}
                  engineers={(() => {
                    // Collect all engineers from hierarchy subordinates and independent engineers
                    const allEngineers: any[] = [];

                    // Add engineers from supervisor subordinates
                    hierarchicalAssignees.supervisors.forEach((sup: any) => {
                      if (sup.subordinates && Array.isArray(sup.subordinates)) {
                        allEngineers.push(...sup.subordinates);
                      }
                    });

                    // Add independent engineers
                    if (hierarchicalAssignees.independentEngineers && Array.isArray(hierarchicalAssignees.independentEngineers)) {
                      allEngineers.push(...hierarchicalAssignees.independentEngineers);
                    }

                    // Remove duplicates based on _id
                    const uniqueEngineers = allEngineers.filter((eng, index, self) =>
                      index === self.findIndex((e) => normalizeId(e._id) === normalizeId(eng._id))
                    );

                    return uniqueEngineers;
                  })()}
                  equipment={equipment}
                  onSelectMember={(member: any) => {
                    setSelectedMember(member);
                    setShowMemberDetails(true);
                  }}
                  onEquipmentCountClick={(equipmentIds: string[]) => {
                    // Highlight equipment on map
                    setSelectedEquipmentIds(equipmentIds);

                    // Auto-expand groups containing the highlighted equipment
                    const targetEquipment = equipment.filter((e: any) => equipmentIds.includes(e._id || e.id));
                    if (targetEquipment.length > 0) {
                      setExpandedGroups(prev => {
                        const newExpanded = { ...prev };
                        targetEquipment.forEach((eq: any) => {
                          const zone = eq.zone || 'Unknown Zone';
                          const subLoc = eq.subLocation || 'No SubLocation';
                          const key = `${zone}-${subLoc}`;
                          newExpanded[key] = true;
                        });
                        return newExpanded;
                      });
                    }
                  }}
                  showRejectedOnly={showRejectedOnly}
                  setShowRejectedOnly={setShowRejectedOnly}
                  resourceApprovalData={resourceApprovalData}
                />
              </div>
            </div>

            {/* Quick Stats Panel */}
            <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
              <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
                <Activity size={20} className="text-blue-500" />
                Assignment Progress Overview
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-gray-600">Total Assigned</span>
                    <span className="font-bold text-gray-900">
                      {equipment.length > 0
                        ? Math.round((equipment.filter((e: any) => e.isAssigned).length / equipment.length) * 100)
                        : 0}%
                    </span>
                  </div>
                  <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-blue-500 to-emerald-500 rounded-full transition-all duration-1000"
                      style={{
                        width: `${equipment.length > 0
                          ? (equipment.filter((e: any) => e.isAssigned).length / equipment.length) * 100
                          : 0}%`
                      }}
                    ></div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-emerald-50 p-4 rounded-xl border border-emerald-100">
                    <div className="text-3xl font-bold text-emerald-600">{equipment.filter((e: any) => e.isAssigned).length}</div>
                    <div className="text-xs text-emerald-700 font-medium mt-1">Assigned Units</div>
                  </div>
                  <div className="bg-orange-50 p-4 rounded-xl border border-orange-100">
                    <div className="text-3xl font-bold text-orange-600">{equipment.length - equipment.filter((e: any) => e.isAssigned).length}</div>
                    <div className="text-xs text-orange-700 font-medium mt-1">Pending</div>
                  </div>
                </div>
                <div className="bg-blue-50 p-4 rounded-xl border border-blue-100">
                  <div className="text-3xl font-bold text-blue-600">{equipment.length}</div>
                  <div className="text-xs text-blue-700 font-medium mt-1">Total Equipment</div>
                </div>
              </div>
            </div>
          </div>

          {/* Member Details Drawer */}
          <RightDrawer
            open={showMemberDetails}
            onClose={() => setShowMemberDetails(false)}
            title="Resource Details"
            size="lg"
          >
            {selectedMember && (
              <div className="p-6 space-y-6">
                {/* Member Header */}
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-6 border border-blue-100">
                  <div className="flex items-start gap-4">
                    <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 text-white flex items-center justify-center font-bold text-3xl shadow-lg flex-shrink-0">
                      {selectedMember.name.charAt(0)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-2xl font-bold text-gray-900 mb-1">{selectedMember.name}</h3>
                      <p className="text-blue-600 font-semibold mb-3 flex items-center gap-2">
                        {(typeof selectedMember.role === 'object' && selectedMember.role ? selectedMember.role.name : selectedMember.role) as any}
                        {selectedMember.role?.name === 'Site Supervisor' && (
                          <Shield size={16} className="text-emerald-600" />
                        )}
                      </p>

                      {/* Contact Info */}
                      <div className="flex flex-wrap items-center gap-4 text-sm">
                        {selectedMember.email && (
                          <span className="flex items-center gap-1.5 text-gray-600 bg-white px-3 py-1.5 rounded-lg border border-gray-200">
                            <User size={14} />
                            {selectedMember.email}
                          </span>
                        )}
                        {selectedMember.mobile && (
                          <span className="flex items-center gap-1.5 text-gray-600 bg-white px-3 py-1.5 rounded-lg border border-gray-200">
                            <Phone size={14} className="text-green-600" />
                            <span className="font-medium">{selectedMember.mobile}</span>
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Quick Stats */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-100 relative overflow-hidden">
                    {isWorkloadLoading && (
                      <div className="absolute inset-0 bg-emerald-50/50 flex items-center justify-center backdrop-blur-[1px]">
                         <Loader2 size={24} className="text-emerald-500 animate-spin" />
                      </div>
                    )}
                    <div className="text-3xl font-bold text-emerald-600">
                      {resourceWorkload ? (
                        resourceWorkload.projects?.find((p: any) => p.projectId === projectId || p.projectCode === projectId)?.equipmentCount || 0
                      ) : (
                        // Fallback while loading
                        selectedMember.allocatedEquipment !== undefined 
                          ? selectedMember.allocatedEquipment 
                          : equipment.filter((e: any) => {
                              const assignment = e.assignedTo || getEquipmentAssignee(e._id || e.id);
                              return assignment && normalizeId(assignment.assigneeId) === normalizeId(selectedMember._id);
                            }).length
                      )}
                    </div>
                    <div className="text-xs text-emerald-700 font-medium mt-1">Current Project Assigned Equipments</div>
                  </div>

                  <div className="bg-blue-50 rounded-xl p-4 border border-blue-100 relative overflow-hidden">
                    {isWorkloadLoading && (
                      <div className="absolute inset-0 bg-blue-50/50 flex items-center justify-center backdrop-blur-[1px]">
                         <Loader2 size={24} className="text-blue-500 animate-spin" />
                      </div>
                    )}
                    <div className="text-3xl font-bold text-blue-600">
                      {resourceWorkload ? (
                        resourceWorkload.totalProjects || 0
                      ) : (
                        // Fallback
                        (selectedMember.otherProjects?.length || selectedMember.assignedProjects?.length || 0) + 1
                      )}
                    </div>
                    <div className="text-xs text-blue-700 font-medium mt-1">Total Projects</div>
                  </div>
                </div>

                {/* Total Project List */}
                <div className="border-t border-gray-100 pt-6">
                  <h4 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
                    <Activity size={18} className="text-blue-600" />
                    Total Project List
                  </h4>

                  <div className="space-y-3 relative min-h-[100px]">
                    {isWorkloadLoading ? (
                      <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-400">
                        <Loader2 size={32} className="animate-spin text-blue-500 mb-2" />
                        <span className="text-sm font-medium">Loading precise cross-project workload...</span>
                      </div>
                    ) : (() => {
                      const displayProjects: any[] = [];
                      
                      // Use resourceWorkload.projects if available
                      if (resourceWorkload?.projects?.length > 0) {
                        displayProjects.push(...resourceWorkload.projects);
                      } else {
                        // Use fallback projects from selectedMember
                        const fallbackProjects = selectedMember?.otherProjects || selectedMember?.assignedProjects || [];
                        displayProjects.push(...fallbackProjects);
                        
                        // Ensure current project is in the list
                        const hasCurrentProject = displayProjects.some((p: any) => p.projectId === projectId || p.projectCode === projectId);
                        
                        if (!hasCurrentProject && project) {
                          const currentProjectCount = selectedMember?.allocatedEquipment !== undefined 
                            ? selectedMember.allocatedEquipment 
                            : equipment.filter((e: any) => {
                                const assignment = e.assignedTo || getEquipmentAssignee(e._id || e.id);
                                return assignment && normalizeId(assignment.assigneeId) === normalizeId(selectedMember?._id);
                              }).length;
                              
                          if (currentProjectCount > 0) {
                            displayProjects.unshift({
                              projectId: projectId,
                              projectName: project.name || project.projectName || 'Current Project',
                              equipmentCount: currentProjectCount
                            });
                          }
                        }
                      }
                      
                      if (displayProjects.length > 0) {
                        return displayProjects.map((proj: any, idx: number) => {
                          const isCurrentProj = proj.projectId === projectId || proj.projectCode === projectId;
                          return (
                            <motion.div
                              key={proj.projectId || idx}
                              initial={{ opacity: 0, x: -20 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: idx * 0.05 }}
                              className={`p-4 rounded-xl border-2 transition-all ${isCurrentProj
                                ? 'bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-300 shadow-md'
                                : 'bg-white border-gray-200 hover:border-blue-300 hover:shadow-sm'
                                }`}
                            >
                              <div className="flex items-start justify-between gap-3">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-2">
                                    <h5 className="font-bold text-gray-900 truncate">
                                      {proj.projectName || 'Unnamed Project'}
                                    </h5>
                                    {isCurrentProj && (
                                      <span className="px-2 py-0.5 bg-emerald-500 text-white text-xs font-bold rounded-full flex-shrink-0">
                                        Current
                                      </span>
                                    )}
                                  </div>

                                  {proj.projectId && (
                                    <div className="text-xs text-gray-500 mb-2 font-mono">
                                      ID: {proj.projectId}
                                    </div>
                                  )}

                                  <div className="flex items-center gap-4 text-sm">
                                    <span className="flex items-center gap-1.5 text-gray-600">
                                      <HardDrive size={14} className="text-blue-500" />
                                      <span className="font-bold text-blue-600">{proj.equipmentCount || 0}</span>
                                      <span className="text-gray-500">Equipment Assigned</span>
                                    </span>
                                  </div>
                                </div>

                                <div className={`text-2xl font-bold flex-shrink-0 ${isCurrentProj ? 'text-emerald-600' : 'text-gray-400'}`}>
                                  {proj.equipmentCount || 0}
                                </div>
                              </div>
                            </motion.div>
                          );
                        });
                      } else {
                        return (
                          <div className="p-6 text-center text-gray-500 bg-gray-50 rounded-xl border border-gray-100 border-dashed">
                            No equipment assigned across any project for this resource.
                          </div>
                        );
                      }
                    })()}
                  </div>
                </div>

              </div>
            )}
          </RightDrawer>

          {/* Assignment Drawer */}
          <RightDrawer
            open={showAssignmentDrawer}
            onClose={() => {
              setShowAssignmentDrawer(false);
              setAssignmentMode('engineer'); // Reset to default mode
              setAssignmentFormData({
                assigneeId: '',
                assigneeRole: 'Site Engineer',
                supervisorId: ''
              });
              setFormErrors({});
            }}
            title={`Assign ${selectedEquipmentIds.length} Equipment`}
            size="md"
          >
            <div className="p-6 space-y-6">
              {/* Assignment Mode Selection */}
              <div className="bg-gray-50 p-1 rounded-xl flex">
                <button
                  className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${assignmentMode === 'engineer' ? 'bg-white shadow-sm text-blue-600' : 'text-gray-500 hover:text-gray-700'
                    }`}
                  onClick={() => {
                    setAssignmentMode('engineer');
                    // Reset form when switching modes
                    setAssignmentFormData({
                      assigneeId: '',
                      assigneeRole: 'Site Engineer',
                      supervisorId: ''
                    });
                    setFormErrors({});
                  }}
                >
                  To Site Engineer
                </button>
                <button
                  className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${assignmentMode === 'supervisor' ? 'bg-white shadow-sm text-blue-600' : 'text-gray-500 hover:text-gray-700'
                    }`}
                  onClick={() => {
                    setAssignmentMode('supervisor');
                    // Reset form when switching modes
                    setAssignmentFormData({
                      assigneeId: '',
                      assigneeRole: 'Site Supervisor',
                      supervisorId: ''
                    });
                    setFormErrors({});
                  }}
                >
                  To Site Supervisor
                </button>
              </div>

              {/* Form Fields */}
              <div className="space-y-4">
                {assignmentMode === 'engineer' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Select Supervisor (Reporting To)</label>
                    <div className="relative">
                      <select
                        value={assignmentFormData.supervisorId}
                        onChange={(e) => setAssignmentFormData({ ...assignmentFormData, supervisorId: e.target.value })}
                        className={`w-full p-3 bg-white border rounded-xl appearance-none outline-none focus:ring-2 focus:ring-blue-500 ${formErrors.supervisorId ? 'border-red-300' : 'border-gray-200'
                          }`}
                      >
                        <option value="">Select Supervisor...</option>
                        {availableSupervisors.map((s: any) => (
                          <option key={s._id} value={s._id}>{s.name}</option>
                        ))}
                      </select>
                      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" size={16} />
                    </div>
                    {formErrors.supervisorId && <p className="text-red-500 text-xs mt-1">{formErrors.supervisorId}</p>}
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Select {assignmentMode === 'engineer' ? 'Engineer' : 'Supervisor'}
                  </label>
                  <div className="relative">
                    <select
                      value={assignmentFormData.assigneeId}
                      onChange={(e) => setAssignmentFormData({ ...assignmentFormData, assigneeId: e.target.value })}
                      className={`w-full p-3 bg-white border rounded-xl appearance-none outline-none focus:ring-2 focus:ring-blue-500 ${formErrors.assigneeId ? 'border-red-300' : 'border-gray-200'
                        }`}
                    >
                      <option value="">Select Assignee...</option>
                      {(assignmentMode === 'engineer' ? availableEngineers : availableSupervisors).map((a: any) => (
                        <option key={a._id} value={a._id}>{a.name}</option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" size={16} />
                  </div>
                  {formErrors.assigneeId && <p className="text-red-500 text-xs mt-1">{formErrors.assigneeId}</p>}
                </div>
              </div>

              {/* Summary */}
              <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                <h4 className="font-bold text-blue-800 mb-2 flex items-center gap-2">
                  <Info size={16} /> Summary
                </h4>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• {selectedEquipmentIds.length} equipment items selected</li>
                  <li>• Assigning to {assignmentMode === 'engineer' ? 'Site Engineer' : 'Site Supervisor'}</li>
                  {assignmentMode === 'engineer' && <li>• Reporting to selected Supervisor</li>}
                </ul>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => setShowAssignmentDrawer(false)}
                  className="flex-1 px-4 py-3 border border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAssignmentSubmit}
                  disabled={isSubmitting}
                  className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 font-bold shadow-lg shadow-blue-500/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isSubmitting ? <Loader2 className="animate-spin" size={20} /> : <><CheckCircle size={20} /> Confirm Assignment</>}
                </button>
              </div>
            </div>
          </RightDrawer>

          {/* Reassignment Confirmation Drawer */}
          <RightDrawer
            open={showReassignmentConfirm}
            onClose={handleReassignmentCancel}
            title="Confirm Reassignment"
            size="md"
          >
            {reassignmentData && (
              <div className="p-6 space-y-6">
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-3">
                  <div className="p-2 bg-amber-100 rounded-lg h-fit text-amber-600">
                    <Shield size={20} />
                  </div>
                  <div>
                    <h4 className="font-bold text-amber-800">Existing Assignments Found</h4>
                    <p className="text-sm text-amber-700 mt-1">
                      Some of the selected equipment is already assigned. Proceeding will overwrite these assignments.
                    </p>
                  </div>
                </div>

                <div className="max-h-60 overflow-y-auto custom-scrollbar border border-gray-200 rounded-xl">
                  {reassignmentData.alreadyAssignedEquipment.map((equipment: any) => (
                    <div key={equipment._id || equipment.id} className="p-3 border-b border-gray-100 last:border-0 flex justify-between items-center bg-white">
                      <div>
                        <div className="font-medium text-gray-900">{equipment.equipmentName || equipment.name}</div>
                        <div className="text-xs text-gray-500">Currently: {equipment.currentAssignee?.assigneeName}</div>
                      </div>
                      <div className="text-xs font-bold text-amber-600 bg-amber-50 px-2 py-1 rounded">
                        Reassigning
                      </div>
                    </div>
                  ))}
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    onClick={handleReassignmentCancel}
                    className="flex-1 px-4 py-3 border border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 font-medium transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleReassignmentConfirm}
                    disabled={isSubmitting}
                    className="flex-1 px-4 py-3 bg-amber-500 text-white rounded-xl hover:bg-amber-600 font-bold shadow-lg shadow-amber-500/30 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {isSubmitting ? <Loader2 className="animate-spin" size={20} /> : 'Confirm Reassignment'}
                  </button>
                </div>
              </div>
            )}
          </RightDrawer>

          {/* Manage Project Resources Drawer */}
          <ManageProjectResources
            isOpen={showManageResources}
            onClose={() => setShowManageResources(false)}
            projectId={projectId}
          />

        </motion.div>
      </div>
    </RightDrawer>
  );
}
export default ProjectAssignments;